import { useEffect, useMemo, useState } from "react";
import {
  Button,
  CheckboxCustom,
  Img,
  Input,
  List,
  Radio,
  Text,
} from "components";
import DesignMyOwnCakeQuestionOneHeader from "components/DesignMyOwnCakeQuestionOneHeader";
import Footer1 from "components/Footer1";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import StepProgress from "components/StepProgress";
import globalRequest from "../../utils/global/globalRequest";
import { useDispatch, useSelector } from "react-redux";
import { changeLoader } from "../../redux/reducers/loader";
import * as API from "../../utils/helper/Enum";
import { setSnackbar } from "../../redux/reducers/snackbar";
import * as CUSTOM from "../../utils/helper/custom";
import { InputAdornment } from "@material-ui/core";
import EmptyAll from "pages/EmptyList";
import { ImageCropper } from "popups/ImageCropper";
import { getAuth } from "../../redux/reducers/loginData";
import DesignMyOwnCakeSummaryPage from "pages/DesignMyOwnCakeSummary";
import { useNavigate } from "react-router-dom";
import {
  addDeleteGetLocalStorage,
  storageKeys,
} from "../../utils/global/localData";
import { get } from "lodash";
import Footer from "components/Footer";
import CUSTOM_CAKE_IDS from "config/customCakeIds";

const Box = styled.div`
  ${({ isSelected }) =>
    isSelected &&
    ` 
      border: 2px solid #771237 !important;
      background-color: #FFF5F8;
`}
`;

// Import Google Fonts
const GlobalStyle = styled.div`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Inter:wght@400;500;600;700&display=swap');
`;

// Main Page Container
const PageWrapper = styled.div`
  background: #FEFBFC;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

// Header Section
const Header = styled.div`
  background: white;
  padding: 32px 5%;
  text-align: start;
  border-bottom: 1px solid #F0F0F0;

  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

const PageTitle = styled.h1`
  font-family: 'Unbounded', Georgia, serif;
  font-size: 44px;
  font-weight: 700;
  color: #771237;
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const PageSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 400;
  color: #8D4660;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Progress Stepper Section (Desktop only)
const StepperSection = styled.div`
  background: #FEFBFC;
  padding: 32px 20px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const StepperWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0px;
  overflow: hidden;
  padding: 10px 0;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  position: relative;
`;

const StepCircle = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $isCompleted, $isActive }) =>
    $isActive ? '#FFE8F0' : $isCompleted ? '#771237' : '#F5F5F5'};
  border: 3px solid ${({ $isCompleted, $isActive }) =>
    $isActive ? '#771237' : $isCompleted ? '#771237' : '#E5E5E5'};
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  box-shadow: ${({ $isActive }) =>
    $isActive ? '0 4px 16px rgba(119, 18, 55, 0.2)' : 'none'};
`;

const StepIconImg = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
  filter: ${({ $isActive }) => $isActive ? 'brightness(0) invert(1)' : 'none'};
`;

const StepNumber = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${({ $isCompleted, $isActive }) =>
    $isCompleted ? 'white' : $isActive ? '#771237' : '#AAAAAA'};
`;

const CheckMark = styled.div`
  color: white;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
`;

const StepLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $isCompleted, $isActive }) =>
    $isCompleted || $isActive ? '#771237' : '#999999'};
  text-align: center;
  white-space: normal;
  max-width: 100%;
  line-height: 1.2;
  letter-spacing: 0;
`;

const StepLine = styled.div`
  height: 3px;
  width: 60px;
  background: ${({ $isCompleted }) => ($isCompleted ? '#771237' : '#E5E5E5')};
  margin-top: -15%;
  transition: all 0.3s ease;
  flex: 0 1 28px;
  min-width: 12px;
`;

// Mobile Step Header
const MobileStepHeader = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    background: white;
    border-bottom: 1px solid #F0F0F0;
  }
`;

const MobileStepInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MobileStepIcon = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
`;

const MobileStepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MobileStepTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
`;

const MobileStepCounter = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #8D4660;
`;

const ViewDetailsBtn = styled.button`
  background: white;
  border: 2px solid #771237;
  color: #771237;
  padding: 10px 20px;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #FFF5F8;
  }

  &:after {
    content: '→';
    font-size: 16px;
  }
`;

// Mobile Preview Modal
const MobilePreviewModal = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    align-items: flex-end;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const MobilePreviewContent = styled.div`
  background: white;
  border-radius: 24px 24px 0 0;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const MobilePreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #F0F0F0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
`;

const MobilePreviewTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1A1A1A;
  margin: 0;
`;

const CloseModalBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #F5F5F5;
  border: none;
  color: #666;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover {
    background: #771237;
    color: white;
  }
`;

const MobilePreviewBody = styled.div`
  padding: 24px;
`;

// Main Content Area
const ContentArea = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  display: grid;
  grid-template-columns: 1fr 450px;
  gap: 40px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    padding: 0 0 80px;
  }
`;

// Left Panel - Question Card
const QuestionPanel = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 0;
    box-shadow: none;
  }
`;

const QuestionContent = styled.div`
  padding: 48px;

  @media (max-width: 640px) {
    padding: 20px 16px;
  }
`;

const QuestionHeader = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 400;
  color: #1A1A1A;
  margin: 0 0 12px 0;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 16px;
    font-weight: 400;
    color: #333333;
    margin: 0 0 16px 0;
  }
`;

// Pinterest Browse Banner
const PinterestBanner = styled.div`
  background: linear-gradient(135deg, #FFF5F8 0%, #FFE8F0 100%);
  border: 2px dashed #FFB8D4;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 36px;

  @media (max-width: 768px) {
    background: white;
    border: 1px solid #E8E8E8;
    border-radius: 12px;
    padding: 16px;
    gap: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
`;

const PinterestIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 14px;
  overflow: hidden;
  flex-shrink: 0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    box-shadow: none;
  }
`;

const PinterestImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PinterestTextArea = styled.div`
  flex: 1;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
`;

const PinterestTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 17px;
  font-weight: 600;
  color: #1A1A1A;
  margin-bottom: 6px;

  @media (max-width: 768px) {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 2px;
    line-height: 1.4;
  }
`;

const PinterestSubtext = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #727783;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 12px;
    color: #999999;
    line-height: 1.3;
  }
`;

const BrowseBtn = styled.a`
  background: #771237;
  padding: 12px 28px;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: white;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: inline-block;

  &:hover {
    background: #A00039;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(119, 18, 55, 0.3);
  }

  @media (max-width: 768px) {
    background: transparent;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    transform: rotate(-45deg)
    height: 24px;
    color: #666666;
    font-size: 18px;

    &:hover {
      background: transparent;
      transform: none;
      box-shadow: none;
      color: #771237;
    }

    &:after {
      content: '→';
    }
  }
`;

const BrowseBtnText = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`;

// Form Elements
const FormLabel = styled.label`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #6D737E;
  display: block;
  margin-bottom: 12px;
  letter-spacing: 0.1px;

  @media (max-width: 768px) {
    font-size: 13px;
    color: #666666;
    margin-bottom: 8px;
  }
`;

const RequiredStar = styled.span`
  color: #771237;
  margin-left: 2px;
`;

// Upload Zone
const UploadZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #D0D0D0;
  border-radius: 16px;
  padding: 56px 32px;
  cursor: pointer;
  transition: all 0.25s ease;
  background: #FAFAFA;
  margin-bottom: 36px;
  position: relative;

  &:hover {
    border-color: #771237;
    background: #FFF8FA;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(119, 18, 55, 0.1);
  }

  input {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 32px 16px;
    border-radius: 12px;
    margin-bottom: 20px;
    background: #FAFAFA;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(119, 18, 55, 0.08);
    }
  }
`;

const UploadIconWrapper = styled.div`
  width: 72px;
  height: 72px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    margin-bottom: 12px;
  }
`;

const UploadIconImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const UploadMainText = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #771237;
  margin-bottom: 8px;
  letter-spacing: -0.2px;

  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 4px;
  }
`;

const UploadSubText = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #727783;
  letter-spacing: 0.1px;

  @media (max-width: 768px) {
    font-size: 12px;
    text-align: center;
    color: #999999;
  }
`;

// Image Grid for uploaded images
const UploadedImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 16px;
  margin-bottom: 36px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 20px;
  }
`;

const ImageItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #F0F0F0;
  transition: all 0.2s ease;

  &:hover {
    border-color: #771237;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(119, 18, 55, 0.15);
  }

  @media (max-width: 768px) {
    border-radius: 8px;
    border: 1px solid #E8E8E8;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(119, 18, 55, 0.1);
    }
  }
`;

const UploadedImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.75);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover {
    background: #771237;
    transform: scale(1.15);
  }

  @media (max-width: 768px) {
    top: 6px;
    right: 6px;
    width: 24px;
    height: 24px;
    font-size: 16px;

    &:hover {
      transform: scale(1.1);
    }
  }
`;

const InputField = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid #E0E0E0;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #A2A6AE;
  transition: all 0.2s ease;
  margin-bottom: 36px;
  background: white;

  &::placeholder {
    color: #AAAAAA;
  }

  &:focus {
    outline: none;
    border-color: #771237;
    box-shadow: 0 0 0 4px rgba(119, 18, 55, 0.1);
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 14px;
    border-radius: 8px;
    margin-bottom: 20px;
    border: 1px solid #E0E0E0;

    &:focus {
      box-shadow: 0 0 0 3px rgba(119, 18, 55, 0.08);
    }
  }
`;

const NavButtonsContainer = styled.div`
  display: flex;
  gap: 20px;
  padding: 36px 48px;
  background: white;
  border-top: 1px solid #F5F5F5;

  @media (max-width: 640px) {
    flex-direction: column;
    padding: 24px;
  }
`;

const NavBtn = styled.button`
  flex: 1;
  padding: 18px 36px;
  border-radius: 30px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  border: none;
  letter-spacing: -0.3px;

  ${({ $variant }) =>
    $variant === 'secondary'
      ? `
    background: white;
    border: 2px solid #E0E0E0;
    color: #666666;
    
    &:hover:not(:disabled) {
      background: #F8F8F8;
      border-color: #771237;
      color: #771237;
      transform: translateY(-2px);
    }
  `
      : `
    background: linear-gradient(135deg, #771237 0%, #A00039 100%);
    color: white;
    box-shadow: 0 4px 16px rgba(119, 18, 55, 0.25);
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #A00039 0%, #771237 100%);
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(119, 18, 55, 0.35);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const PreviewPanel = styled.div`
  background: white;
  border-radius: 20px;
  padding: 36px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 24px;

  @media (max-width: 1024px) {
    position: relative;
    top: 0;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const PreviewHeader = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1A1A1A;
  margin: 0 0 8px 0;
  letter-spacing: -0.4px;
`;

const PreviewDescription = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #999999;
  margin: 0 0 28px 0;
  line-height: 1.6;
`;

const PreviewDivider = styled.div`
  height: 1px;
  background: #F0F0F0;
  margin: 24px 0;
`;

const PreviewBox = styled.div`
  background: #F9F9F9;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
`;

const PreviewBoxTitle = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #666666;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const PreviewBoxValue = styled.div`
  font-family: 'Inter', sans-serif;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  color: #A2A6AE;
  line-height: 28px;
  word-break: break-word;
`;

const PreviewImageContainer = styled.div`
  background: linear-gradient(160deg, #F8F8F8 20%, #F9E5EC 40%, #F0F0F0 100%);
  border-radius: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
`;

const PreviewCakeImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  height: 240px;
  filter: drop-shadow(0 8px 24px rgba(119, 18, 55, 0.15));
`;

const PreviewSizeLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
  text-align: center;
  letter-spacing: 0.2px;
  margin-top: 16px;
`;

const TotalContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
  margin-top: 24px;
  border-top: 2px solid #F0F0F0;
`;

const TotalText = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1A1A1A;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TotalPrice = styled.div`
  font-family: 'Unbounded', sans-serif;
  display: flex;
  align-items: center;
  font-size: 24px;
  font-weight: 700;
  color: #771237;
  letter-spacing: -0.5px;
`;

// Option Item
const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 32px;
  width: 100%;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const OptionBox = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  border: 3px solid ${({ $isSelected }) => ($isSelected ? '#771237' : '#E8E8E8')};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  overflow: hidden;
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? '0 6px 20px rgba(119, 18, 55, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.06)'};

  &:hover {
    border-color: #771237;
    transform: translateY(-6px);
    box-shadow: 0 8px 24px rgba(119, 18, 55, 0.25);
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
    filter: grayscale(1);
  `}
`;

const OptionImageWrapper = styled.div`
  width: 100%;
  height: 200px;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 24px;
`;

const OptionImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;

  ${OptionBox}:hover & {
    transform: scale(1.08);
  }
`;

const OptionInfo = styled.div`
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ $isSelected }) => ($isSelected ? '#FFF5F8' : 'white')};
`;

const OptionLeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
`;

const OptionTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OptionName = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
  letter-spacing: -0.3px;
`;

const OptionSubtext = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #666666;
`;

const OptionPriceTag = styled.div`
  font-family: 'Unbounded', sans-serif;
  font-size: 18px;
  display: flex;
  align-items: center;
  font-weight: 700;
  color: #1A1A1A;
  letter-spacing: -0.3px;
  white-space: nowrap;
`;

const QuestionSubtext = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #666666;
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const SarIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 6px;
  object-fit: contain;
`;

const QuantityOptionBox = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  border: 3px solid ${({ $isSelected }) => ($isSelected ? '#771237' : '#E8E8E8')};
  border-radius: 20px;
  transition: all 0.3s ease;
  background: white;
  overflow: hidden;
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? '0 6px 20px rgba(119, 18, 55, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.06)'};

  &:hover {
    border-color: #771237;
    transform: translateY(-6px);
    box-shadow: 0 8px 24px rgba(119, 18, 55, 0.25);
  }
`;

const QuantityControlArea = styled.div`
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ $isSelected }) => ($isSelected ? '#FFF5F8' : 'white')};
`;

const QuantityLeftSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const QuantityCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  border: 1px solid #A2A6AE;
  border-radius: 30px;
  padding: 4px 12px;
  justify-content: center;
`;

const CounterButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 2px solid #E0E0E0;
  background: white;
  color: #771237;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover:not(:disabled) {
    border-color: #771237;
    background: #FFF5F8;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const CounterValue = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1A1A1A;
  min-width: 40px;
  text-align: center;
`;

const QuantitySubtext = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #999999;
  margin-top: 4px;
`;

// Message Input Components
const MessageInputBox = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  border: 3px solid ${({ $hasMessage }) => ($hasMessage ? '#771237' : '#E8E8E8')};
  border-radius: 20px;
  transition: all 0.3s ease;
  background: white;
  box-shadow: ${({ $hasMessage }) =>
    $hasMessage ? '0 6px 20px rgba(119, 18, 55, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.06)'};
  margin-bottom: 24px;
`;

const MessageLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MessageTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 16px;
  border: 2px solid #E0E0E0;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #1A1A1A;
  resize: vertical;
  transition: all 0.2s ease;
  margin-top: 12px;

  &::placeholder {
    color: #AAAAAA;
  }

  &:focus {
    outline: none;
    border-color: #771237;
    box-shadow: 0 0 0 4px rgba(119, 18, 55, 0.1);
  }
`;

const STORAGE_KEYS = {
  CAKE_DATA: 'custom_cake_data',
  SELECTED_DATA: 'custom_cake_selected',
  IMAGES: 'custom_cake_images',
  REFERENCE: 'custom_cake_reference',
  TEXT: 'custom_cake_text',
  ACTIVE_STEP: 'custom_cake_step',
  COMPLETED_STEPS: 'custom_cake_completed',
  TOTAL_PRICE: 'custom_cake_price',
  SELECTED_SHAPE: 'custom_cake_shape',
  SIZE_TIER_INDEX: 'custom_cake_size_tier_index'
};

const MULTI_LAYER_QUESTION_ID = 8;
const MULTI_LAYER_BACKEND_GROUP_ID = CUSTOM_CAKE_IDS.LAYER_GROUP_ID;

const getCakeSizeInches = (name) => {
  const normalizedName = String(name || "").replace(/[٠-٩]/g, (digit) =>
    String("٠١٢٣٤٥٦٧٨٩".indexOf(digit))
  );
  const size = normalizedName.match(/\d+(?:\.\d+)?/);
  return size ? Number(size[0]) : null;
};

const sortSizeOptions = (options = []) =>
  [...options].sort((a, b) => {
    const aSize = Number(a?.sizeInches ?? Number.MAX_SAFE_INTEGER);
    const bSize = Number(b?.sizeInches ?? Number.MAX_SAFE_INTEGER);
    if (aSize !== bSize) return aSize - bSize;
    return String(a?.name || "").localeCompare(String(b?.name || ""));
  });

const getLayerTierCount = (name) => {
  const normalizedName = String(name || "").toLowerCase();
  if (/(?:\b3\b|three|triple|ثلاث|٣)/i.test(normalizedName)) return 3;
  if (/(?:\b2\b|two|double|multi|ثنائي|طبقتين|٢)/i.test(normalizedName)) return 2;
  return 1;
};

const getShapeKey = (shapeName) => {
  const normalizedShape = String(shapeName || "").toLowerCase();

  if (normalizedShape.includes("square")) return "square";
  if (normalizedShape.includes("heart")) return "heart";
  return "round";
};

const getShapeFolderName = (shapeName) => {
  const shapeKey = getShapeKey(shapeName);
  if (shapeKey === 'square') return 'square';
  if (shapeKey === 'heart') return 'heart';
  return 'round';
};

const getTierFolderName = (shapeName, tierCount = 1) => {
  const shapeKey = getShapeKey(shapeName);
  const count = Math.max(1, Number(tierCount) || 1);
  if (shapeKey === 'square') {
    return `Tier${count}`;
  }
  return `tier${count}`;
};

// const getScopedTierImage = (shapeName, tierCount, fileName, fallbackPath = '/images/roundCake.png') => {
//   const shapeFolder = getShapeFolderName(shapeName);
//   const tierFolder = getTierFolderName(shapeName, tierCount);
//   const scopedPath = `/images/${shapeFolder}/${tierFolder}/${fileName}`;
//   return scopedPath;
// };

const getShapeCakeImage = (shapeName, tierCount = 1, isSizeOption = false) => {
  const shapeKey = getShapeKey(shapeName);
  const effectiveTier = isSizeOption ? 1 : Math.max(1, Number(tierCount) || 1);

  if (shapeKey === "square") {
    if (effectiveTier >= 3) return '/images/square/Tier3/squareTier3.png';
    if (effectiveTier === 2) return '/images/square/Tier2/squareTier2.png';
    return '/images/square/Tier1/squareCake.png';
  }

  if (shapeKey === "heart") return '/images/heartCake.png';

  if (effectiveTier >= 3) return '/images/round/tier3/roundCakeTier3.png';
  if (effectiveTier === 2) return '/images/round/tier2/roundCakeTier2.png';
  return '/images/round/tier1/roundCake1.png';
};

const getFlavorCakeImage = (flavorOption, shapeName, tierCount = 1) => {
  const shapeKey = getShapeKey(shapeName);
  const effectiveTier = Math.max(1, Number(tierCount) || 1);
  const isMulti = effectiveTier > 1;
  const flavorName = String(flavorOption?.name || "").toLowerCase();
  const isChocolate =
    flavorOption?.id === 32 ||
    flavorOption?.envItemId === CUSTOM_CAKE_IDS.FLAVOR_CHOCOLATE_ID ||
    flavorName.includes("choco");

  if (shapeKey === "square") {
    if (isMulti) {
      if (effectiveTier >= 3) {
        return isChocolate
          ? '/images/square/Tier3/squareChocolateTier3.png'
          : '/images/square/Tier3/squareStrawberryTier3.png';
      }
      return isChocolate
        ? '/images/square/Tier2/squareChocolateTier2.png'
        : '/images/square/Tier2/squareStrawberryTier2.png';
    }
    return isChocolate
      ? '/images/square/Tier1/squareChocolateTier1.png'
      : '/images/square/Tier1/squareStrawberryTier1.png';
  }

  if (shapeKey === "heart") {
    return isChocolate ? '/images/chokoflawerHeart.png' : '/images/strawberryFlawerHeart.png';
  }

  // round
  if (isMulti) {
    if (effectiveTier >= 3) {
      return isChocolate
        ? '/images/round/tier3/chokolateRoundTier3.png'
        : '/images/round/tier3/strawberryRoundTier3.png';
    }
    return isChocolate
      ? '/images/round/tier2/chocolateRoundTier2.png'
      : '/images/round/tier2/strawberryRound2Tier.png';
  }
  return isChocolate
    ? '/images/round/tier1/chokoflawerRound.png'
    : '/images/round/tier1/strawberryFlawerRound.png';
};

const get3DPiecesCakeImage = (quantity, shapeName, tierCount = 1) => {
  const shapeKey = getShapeKey(shapeName);
  const effectiveTier = Math.max(1, Number(tierCount) || 1);
  const isMulti = effectiveTier > 1;
  const rawQty = Number(quantity);
  const qty = Number.isNaN(rawQty) ? 0 : rawQty;

  if (qty <= 0) {
    return getShapeCakeImage(shapeName, tierCount, false);
  }

  if (shapeKey === "square") {
    if (isMulti) {
      if (effectiveTier >= 3) {
        if (qty === 1) return '/images/square/Tier3/1-3D-SquareTier3.png';
        if (qty === 2) return '/images/square/Tier3/2-3D-SquareTier3.png';
        return '/images/square/Tier3/3-3D-SquareTier3.png';
      }
      if (qty === 1) return '/images/square/Tier2/3Ddesign1SquareTier2.png';
      if (qty === 2) return '/images/square/Tier2/3Ddesign2SquareTier2.png';
      return '/images/square/Tier2/3Ddesign3SquareTier2.png';
    }
    if (qty === 1) return '/images/square/Tier1/3D_Square_1Tier.png';
    if (qty === 2) return '/images/square/Tier1/3D_Square_2ndTier2.png';
    return '/images/square/Tier1/3D_Square_3rdTier3.png';
  }

  if (shapeKey === "heart") {
    return '/images/3DHeart.png';
  }

  // round
  if (isMulti) {
    if (effectiveTier >= 3) {
      if (qty === 1) return '/images/round/tier3/1-3D-RoundTier3.png';
      if (qty === 2) return '/images/round/tier3/2-3D-RoundTier3.png';
      return '/images/round/tier3/3-3D-RoundTier3.png';
    }
    if (qty === 1) return '/images/round/tier2/3D-1design-Tier2.png';
    if (qty === 2) return '/images/round/tier2/3D-2design-Tier2.png';
    return '/images/round/tier2/3D-3design-tier3.png';
  }

  if (qty === 1) return '/images/round/tier1/3D_rounde_1st.png';
  if (qty === 2) return '/images/round/tier1/3D_rounde_2nd.png';
  return '/images/round/tier1/3D_rounde_3rd.png';
};

const getGlitterCakeImage = (glitterOption, shapeName, tierCount = 1, qty3D = 0) => {
  const shapeKey = getShapeKey(shapeName);
  const effectiveTier = Math.max(1, Number(tierCount) || 1);
  const isMulti = effectiveTier > 1;
  const glitterName = String(glitterOption?.name || "").toLowerCase();
  const isSilver =
    glitterOption?.id === 52 ||
    glitterOption?.envItemId === CUSTOM_CAKE_IDS.GLITTER_SILVER_ID ||
    glitterName.includes("silver");
  const qtySuffix = qty3D >= 3 ? 3 : (qty3D > 0 ? qty3D : 1);

  if (shapeKey === "square") {
    if (isMulti) {
      if (effectiveTier >= 3) {
        if (isSilver) {
          if (qtySuffix === 1) return '/images/square/Tier3/silver-1-3D-squareTier3.png';
          if (qtySuffix === 2) return '/images/square/Tier3/silver-2-3D-squareTier3.png';
          return '/images/square/Tier3/silver-3-3D-squareTier3.png';
        }
        if (qtySuffix === 1) return '/images/square/Tier3/golden-1-3D-SquareTier3.png';
        if (qtySuffix === 2) return '/images/square/Tier3/golden-2-3D-SquareTier3.png';
        return '/images/square/Tier3/golden-3-3D-SquareTier3.png';
      }
      if (isSilver) {
        if (qtySuffix === 1) return '/images/square/Tier2/silver3Ddesign1SquareTier2.png';
        if (qtySuffix === 2) return '/images/square/Tier2/silver3Ddesign2SquareTier2.png';
        return '/images/square/Tier2/silver3Ddesign3SquareTier2.png';
      }
      if (qtySuffix === 1) return '/images/square/Tier2/golden3Ddesing1SquareTier2.png';
      if (qtySuffix === 2) return '/images/square/Tier2/golden3Ddesign2SquareTier2.png';
      return '/images/square/Tier2/golden3Ddesign3SquareTier2.png';
    }
    // square tier1
    if (isSilver) {
      if (qtySuffix === 1) return '/images/square/Tier1/silver3Ddesign1SquareTier1.png';
      if (qtySuffix === 2) return '/images/square/Tier1/silver3Ddesign2SquareTier1.png';
      return '/images/square/Tier1/silver3Ddesign3SquareTier1.png';
    }
    if (qtySuffix === 1) return '/images/square/Tier1/golden3Ddesign1SquareTier1.png';
    if (qtySuffix === 2) return '/images/square/Tier1/golden3Ddesign2SquareTier1.png';
    return '/images/square/Tier1/golden3Ddesign3SquareTier1.png';
  }

  if (shapeKey === "heart") {
    return isSilver ? '/images/glitterSilverHeart.png' : '/images/glitterGoldenHeart.png';
  }

  // round
  if (isMulti) {
    if (effectiveTier >= 3) {
      if (isSilver) {
        if (qtySuffix === 1) return '/images/round/tier3/silver-1-3D-RoundTier3.png';
        if (qtySuffix === 2) return '/images/round/tier3/silver-2-3D-RoundTier3.png';
        return '/images/round/tier3/silver-3-3D-RoundTier3.png';
      }
      if (qtySuffix === 1) return '/images/round/tier3/golden-1-3D-RoundTier3.png';
      if (qtySuffix === 2) return '/images/round/tier3/golden-2-3D-RoundTier3.png';
      return '/images/round/tier3/golden-3-3D-RoundTier3.png';
    }
    if (isSilver) {
      if (qtySuffix === 1) return '/images/round/tier2/silver3D-1Design-RoundTier2.png';
      if (qtySuffix === 2) return '/images/round/tier2/silver3D-2Design-RoundTier2.png';
      return '/images/round/tier2/silver3-3DRoundTier2.png';
    }
    if (qtySuffix === 1) return '/images/round/tier2/golden1-3DRoundTier2.png';
    if (qtySuffix === 2) return '/images/round/tier2/golden2-3DRoundTier2.png';
    return '/images/round/tier2/golden3-3D-RoundTier2.png';
  }

  // round tier1
  if (isSilver) {
    return `/images/round/tier1/silver_glitter_${qtySuffix}_3D.png`;
  }
  return `/images/round/tier1/golden_glitter_${qtySuffix}_3D.png`;
};

const getRibbonCakeImage = (quantity, shapeName, tierCount = 1, isSilverGlitter = false) => {
  const shapeKey = getShapeKey(shapeName);
  const effectiveTier = Math.max(1, Number(tierCount) || 1);
  const isMulti = effectiveTier > 1;
  const rawQty = Number(quantity);
  const qty = Number.isNaN(rawQty) ? 0 : rawQty;

  if (qty <= 0) {
    return getShapeCakeImage(shapeName, tierCount, false);
  }

  const ribbonCount = Math.min(Math.max(1, qty), 4);

  if (shapeKey === "square") {
    if (isMulti) {
      if (effectiveTier >= 3) {
        if (isSilverGlitter) {
          if (ribbonCount >= 4) return '/images/square/Tier3/silver-4-RibbonSquareTier3.png';
          if (ribbonCount === 3) return '/images/square/Tier3/silver-3-RibbonSquareTier3.png';
          if (ribbonCount === 2) return '/images/square/Tier3/silver-2-RibbonSquareTier3.png';
          return '/images/square/Tier3/siver-1-RibbonSquareTier3.png';
        }
        if (ribbonCount === 1) return '/images/square/Tier3/golden-1-RibbonSquareTier3.png';
        if (ribbonCount === 2) return '/images/square/Tier3/golden-2-RibbonSquareTier3.png';
        if (ribbonCount === 3) return '/images/square/Tier3/golden-3-RibbonSquareTier3.png';
        return '/images/square/Tier3/golden-4-RibbonSquareTier3.png';
      }
      if (isSilverGlitter) {
        if (ribbonCount >= 3) return '/images/square/Tier2/silverRibbon3SquareTier2.png';
        if (ribbonCount === 2) return '/images/square/Tier2/silverRibbon2SquareTier2.png';
        return '/images/square/Tier2/silverRibbon1SquareTier2.png';
      }
      if (ribbonCount === 1) return '/images/square/Tier2/goldenRibbon1SquareTier2.png';
      if (ribbonCount === 2) return '/images/square/Tier2/goldenRibbon2SquareTier2.png';
      if (ribbonCount === 3) return '/images/square/Tier2/goldenRibbon3SquareTier2.png';
      return '/images/square/Tier2/goldenRibbon4SquareTier2.png';
    }
    if (isSilverGlitter) {
      return `/images/square/Tier1/silverSquare${ribbonCount}RibbonTier1.png`;
    }
    return `/images/square/Tier1/goldenSquare${ribbonCount}RibbonTier1.png`;
  }

  if (shapeKey === "heart") {
    return `/images/heart_ribbon_${ribbonCount}.png`;
  }

  // round
  if (isMulti) {
    if (effectiveTier >= 3) {
      if (isSilverGlitter) {
        if (ribbonCount === 1) return '/images/round/tier3/silver-1Ribbon-RoundTier3.png';
        if (ribbonCount === 2) return '/images/round/tier3/silver-2Ribbon-RoundTier3.png';
        if (ribbonCount === 3) return '/images/round/tier3/silver-3Ribbon-RoundTier3.png';
        return '/images/round/tier3/silver-4Ribbon-RoundTier3.png';
      }
      if (ribbonCount === 1) return '/images/round/tier3/golden-1Ribbon-RoundTier3.png';
      if (ribbonCount === 2) return '/images/round/tier3/golden-2Ribbon-RoundTier3.png';
      if (ribbonCount === 3) return '/images/round/tier3/golden-3Ribbon-RoundTier3.png';
      return '/images/round/tier3/golden-4Ribbon-RoundTier3.png';
    }
    if (isSilverGlitter) {
      if (ribbonCount === 3) return '/images/round/tier2/silver2RibbonRoundTier2.png';
      return `/images/round/tier2/silver${ribbonCount}RibbonRoundTier2.png`;
    }
    return `/images/round/tier2/golden${ribbonCount}RibbonRoundTier2.png`;
  }

  // round tier1
  if (isSilverGlitter) {
    return `/images/round/tier1/silver_${ribbonCount}_ribbon.png`;
  }
  return `/images/round/tier1/golden_${ribbonCount}_ribbon.png`;
};

const getSizeQuestionForShapeAndTier = (backendQuestions, selectedShapeName, tierCount) => {
  const normalizedShape = String(selectedShapeName || "").toLowerCase();
  const layerCount = Number(tierCount) || 1;

  const matchingQuestions = (backendQuestions || []).filter((question) => {
      const questionName = (question?.modifierGroupLocales || [])
      .map((locale) => String(locale?.name || ""))
      .join(" ")
      .toLowerCase();

    if (!questionName.includes("size")) return false;
    if (normalizedShape && !questionName.includes(normalizedShape)) return false;

    const isMultiName = /(multi|tier\s*2|tier\s*3|double|two|three|triple)/i.test(
      questionName.replace(/[^a-z0-9]/g, " ")
    );
    return layerCount === 1 ? !isMultiName : isMultiName;
  });

  if (!matchingQuestions.length) return null;

  return matchingQuestions.sort((a, b) => Number(a.id) - Number(b.id))[0];
};

const STEP_CONFIG = {
  1: { label: "stepLabel_size", icon: 2 },
  2: { label: "stepLabel_shape", icon: 3 },
  3: { label: "stepLabel_flavor", icon: 4 },
  4: { label: "stepLabel_design3d", icon: 5 },
  5: { label: "stepLabel_glitter", icon: 6 },
  6: { label: "stepLabel_ribbon", icon: 7 },
  7: { label: "stepLabel_message", icon: 8 },
  [MULTI_LAYER_QUESTION_ID]: { label: "stepLabel_layer", icon: 9 },
};

const STATIC_QUESTIONS = [
  {
    id: 1,
    name: "q_size_title",
    type: "single",
    min: 1,
    max: 1,
    uiOrder: 1,
    envGroupId: CUSTOM_CAKE_IDS.SIZE_GROUP_ID,
    options: [
      {
        id: 11, name: "5-inch", subtext: "opt_size_5inch_sub", price: 99, image: "/images/round/tier1/roundCake1.png", sizeInches: 5,
        envItemId: CUSTOM_CAKE_IDS.SIZE_5INCH_ID,
      },
      {
        id: 12,
        name: "6-inch",
        subtext: "opt_size_6inch_sub",
        price: 199,
        image: "/images/round/tier1/roundCake1.png",
        sizeInches: 6,
        envItemId: CUSTOM_CAKE_IDS.SIZE_6INCH_ID,
      },
      {
        id: 13,
        name: "8-inch",
        subtext: "opt_size_8inch_sub",
        price: 350,
        image: "/images/round/tier1/roundCake1.png",
        sizeInches: 8,
        envItemId: CUSTOM_CAKE_IDS.SIZE_8INCH_ID,
      },
      {
        id: 14,
        name: "10-inch",
        price: 499,
        image: "/images/round/tier1/roundCake1.png",
        sizeInches: 10,
        envItemId: CUSTOM_CAKE_IDS.SIZE_10INCH_ID,
      }
    ]
  },
  {
    id: 2,
    name: "q_shape_title",
    type: "single",
    min: 1,
    max: 1,
    uiOrder: 2,
    envGroupId: CUSTOM_CAKE_IDS.SHAPE_GROUP_ID,
    options: [
      {
        id: 21,
        name: "Square",
        price: 0,
        image: "/images/square/Tier1/squareCake.png",
        shapeKey: "square",
        envItemId: CUSTOM_CAKE_IDS.SHAPE_SQUARE_ID,
      },
      {
        id: 22,
        name: "Round",
        price: 0,
        image: "/images/round/tier1/roundCake1.png",
        shapeKey: "round",
        envItemId: CUSTOM_CAKE_IDS.SHAPE_ROUND_ID,
      },
      // { id: 23, name: "Heart",  price: 0, image: "/images/heartCake.png",
      //   shapeKey: "heart",
      //   envItemId: CUSTOM_CAKE_IDS.SHAPE_HEART_ID,
      // },
    ]
  },
  {
    id: 3,
    name: "q_flavor_title",
    type: "single",
    min: 1,
    max: 1,
    uiOrder: 3,
    envGroupId: CUSTOM_CAKE_IDS.FLAVOR_GROUP_ID,
    options: [
      // {
      //   id: 31,
      //   name: "Vanilla",
      //   price: 51,
      //   envItemId: null,
      //   images: { square: "/images/flawer1.png", round: "/images/venillaFlawerRound.png", heart: "/images/venillaFlawerHeart.png" }
      // },
      {
        id: 32,
        name: "chocolate",
        price: 0,
        envItemId: CUSTOM_CAKE_IDS.FLAVOR_CHOCOLATE_ID,
        images: {
          square: "/images/square/Tier1/squareChocolateTier1.png",
          round: "/images/round/tier1/chokoflawerRound.png",
          heart: "/images/chokoflawerHeart.png",
          squareTier2: "/images/square/Tier2/squareChocolateTier2.png",
          roundTier2: "/images/round/tier2/chocolateRoundTier2.png",
          squareTier3: "/images/square/Tier3/squareChocolateTier3.png",
          roundTier3: "/images/round/tier3/chokolateRoundTier3.png",
        }
      },
      {
        id: 33,
        name: "strawberry",
        price: 0,
        envItemId: CUSTOM_CAKE_IDS.FLAVOR_STRAWBERRY_ID,
        images: {
          square: "/images/square/Tier1/squareStrawberryTier1.png",
          round: "/images/round/tier1/strawberryFlawerRound.png",
          heart: "/images/strawberryFlawerHeart.png",
          squareTier2: "/images/square/Tier2/squareStrawberryTier2.png",
          roundTier2: "/images/round/tier2/strawberryRound2Tier.png",
          squareTier3: "/images/square/Tier3/squareStrawberryTier3.png",
          roundTier3: "/images/round/tier3/strawberryRoundTier3.png",
        }
      },
      // {
      //   id: 34,
      //   name: "Berry",
      //   price: 76,
      //   envItemId: null,
      //   images: { square: "/images/flawer4.png", round: "/images/berryFlawerRound.png", heart: "/images/berryFlawerHeart.png" }
      // }
    ]
  },
  {
    id: 4,
    name: "q_3d_title",
    subtext: "q_3d_sub",
    type: "quantity",
    min: 0,
    max: 0,
    uiOrder: 4,
    envGroupId: CUSTOM_CAKE_IDS.D3_GROUP_ID,
    envQuantityMap: {
      0: CUSTOM_CAKE_IDS.D3_0SHAPE_ID,
      1: CUSTOM_CAKE_IDS.D3_1SHAPE_ID,
      2: CUSTOM_CAKE_IDS.D3_2SHAPE_ID,
      3: CUSTOM_CAKE_IDS.D3_3SHAPE_ID,
    },
    options: [
      {
        id: 41,
        name: "3D Pieces",
        subtext: "opt_3d_pieces_sub",
        price: 50,
        envItemId: CUSTOM_CAKE_IDS.D3_1SHAPE_ID,
        images: {
          square: "/images/square/Tier1/3D_Square_1Tier.png",
          round: "/images/round/tier1/3D_rounde_1st.png",
          heart: "/images/3DHeart.png",
          squareTier2: "/images/square/Tier2/3Ddesign1SquareTier2.png",
          roundTier2: "/images/round/tier2/3D-1design-Tier2.png",
          squareTier3: "/images/square/Tier3/1-3D-SquareTier3.png",
          roundTier3: "/images/round/tier3/1-3D-RoundTier3.png",
        },
        needsShape: true
      }
    ]
  },
  {
    id: 5,
    name: "q_glitter_title",
    subtext: "q_glitter_sub",
    type: "single",
    min: 0,
    max: 0,
    uiOrder: 5,
    envGroupId: CUSTOM_CAKE_IDS.GLITTER_GROUP_ID,
    options: [
      {
        id: 51,
        name: "opt_glitter_golden",
        price: 30,
        envItemId: CUSTOM_CAKE_IDS.GLITTER_GOLDEN_ID,
        images: {
          square: "/images/square/Tier1/golden3Ddesign1SquareTier1.png",
          round: "/images/round/tier1/golden_glitter_1_3D.png",
          heart: "/images/glitterGoldenHeart.png",
          squareTier2: "/images/square/Tier2/golden3Ddesing1SquareTier2.png",
          roundTier2: "/images/round/tier2/golden1-3DRoundTier2.png",
          squareTier3: "/images/square/Tier3/golden-1-3D-SquareTier3.png",
          roundTier3: "/images/round/tier3/golden-1-3D-RoundTier3.png",
        }
      },
      {
        id: 52,
        name: "opt_glitter_silver",
        price: 30,
        envItemId: CUSTOM_CAKE_IDS.GLITTER_SILVER_ID,
        images: {
          square: "/images/square/Tier1/silver3Ddesign1SquareTier1.png",
          round: "/images/round/tier1/silver_glitter_1_3D.png",
          heart: "/images/glitterSilverHeart.png",
          squareTier2: "/images/square/Tier2/silver3Ddesign1SquareTier2.png",
          roundTier2: "/images/round/tier2/silver3D-1Design-RoundTier2.png",
          squareTier3: "/images/square/Tier3/silver-1-3D-squareTier3.png",
          roundTier3: "/images/round/tier3/silver-1-3D-RoundTier3.png",
        }
      },
      // { id: 53, name: "Pinky Color", price: 10, envItemId: null, images: {...} },
      // { id: 54, name: "Bronzy Color", price: 10, envItemId: null, images: {...} },
      // { id: 55, name: "Navy Color", price: 10, envItemId: null, images: {...} },
      // { id: 56, name: "Baby Blue Color", price: 10, envItemId: null, images: {...} }
    ]
  },
  {
    id: 6,
    name: "q_ribbon_title",
    subtext: "q_ribbon_sub",
    type: "quantity",
    min: 0,
    max: 0,
    uiOrder: 6,
    envGroupId: CUSTOM_CAKE_IDS.RIBBON_GROUP_ID,
    envQuantityMap: {
      0: CUSTOM_CAKE_IDS.RIBBON_NO_ID,
      1: CUSTOM_CAKE_IDS.RIBBON_1PC_ID,
      2: CUSTOM_CAKE_IDS.RIBBON_2PC_ID,
      3: CUSTOM_CAKE_IDS.RIBBON_3PC_ID,
      4: CUSTOM_CAKE_IDS.RIBBON_4PC_ID,
    },
    options: [
      {
        id: 61,
        name: "opt_ribbon",
        price: 10,
        envItemId: CUSTOM_CAKE_IDS.RIBBON_1PC_ID,
        images: {
          square: "/images/square/Tier1/goldenSquare1RibbonTier1.png",
          round: "/images/round/tier1/golden_1_ribbon.png",
          roundTier2: "/images/round/tier2/golden1RibbonRoundTier2.png",
          squareTier2: "/images/square/Tier2/goldenRibbon1SquareTier2.png",
          roundTier3: "/images/round/tier3/golden-1Ribbon-RoundTier3.png",
          squareTier3: "/images/square/Tier3/golden-1-RibbonSquareTier3.png",
          heart: "/images/ribbonHeart.png"
        },
        needsShape: true
      }
    ]
  },
  {
    id: 7,
    name: "q_message_title",
    subtext: "q_message_sub",
    type: "message",
    min: 0,
    max: 0,
    uiOrder: 7,
    envGroupId: CUSTOM_CAKE_IDS.MESSAGE_GROUP_ID,
    options: [
      {
        id: 71,
        name: "opt_message_card",
        price: 10,
        image: "/images/specialGift.png",
        envItemId: CUSTOM_CAKE_IDS.MESSAGE_PRINTING_ID,
      }
    ]
  }
];

const DesignMyOwnCakeQuestionOnePage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  let dispatch = useDispatch();
  const [imageForCrop, setImageForCrop] = useState("");
  const [allSelectedImages, setAllSelectedImages] = useState([]);
  const [settingData, setSettingData] = useState({ searchLink: "https://in.pinterest.com/rabbitholesa/?invite_code=399f0ea3a49348dea5272cf4467ddabf" });
  const [reference, setReference] = useState("");
  const [textOnCake, setTextOnCake] = useState("");
  const [selectedData, setSelectedData] = useState([]);
  const [activeId, setActiveId] = useState(-1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [selectedShape, setSelectedShape] = useState(null);
  const [sizeTierIndex, setSizeTierIndex] = useState(0);
  const [quantities, setQuantities] = useState({});
  const [messageCardText, setMessageCardText] = useState("");
  const [allInputData, setAllInputData] = useState({});
  const [backendQuestions, setBackendQuestions] = useState([]);
  const [multiLayerQuestion, setMultiLayerQuestion] = useState(null);
  const [idMapping, setIdMapping] = useState({});
  const [apiStatus, setApiStatus] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [isStorageHydrated, setIsStorageHydrated] = useState(false);
  const auth = useSelector(getAuth);

  const getSafeOptions = (question) => (question?.options || []).filter(Boolean);

  // Step labels
  const wizardQuestions = useMemo(() => {
    const backendShapeQuestion = backendQuestions.find(
      (question) => Number(question?.id) === CUSTOM_CAKE_IDS.SHAPE_GROUP_ID
    );
    const staticShapeQuestion = STATIC_QUESTIONS.find((question) => question.id === 2);
    const shapeOptions = (staticShapeQuestion?.options || []).filter(Boolean).map((option) => {
      const optionName = String(option?.name || "").toLowerCase().trim();
      const items = backendShapeQuestion?.modifierGroupItems || [];
      const backendItem =
        items.find((item) => {
          const n = CUSTOM.getdataByLangKey(item?.modifierGroupItemLocales, "", "name");
          return String(n || "").toLowerCase().trim() === optionName;
        }) ||
        items.find((item) => Number(item?.id) === Number(option?.envItemId));

      return {
        ...option,
         price: backendItem ? Number(backendItem.price) || 0 : option.price,
        nextQuestionId: Number(backendItem?.nextQuestionId) ||
          Number(backendShapeQuestion?.nextQuestionId) || null,
      };
    });
    const selectedShapeId = selectedData.find((group) => group.group_id === 2)?.items?.[0];
    const selectedShapeOption = shapeOptions.filter(Boolean).find((option) => option?.id === selectedShapeId);
    const selectedShapeName = selectedShapeOption?.name || selectedShape || "";
    const tierQuestionId = selectedShapeOption?.nextQuestionId || multiLayerQuestion?.envGroupId;
    const backendTierQuestion = backendQuestions.find(
      (question) => Number(question?.id) === Number(tierQuestionId)
    );
    const tierQuestion = backendTierQuestion
      ? {
          id: MULTI_LAYER_QUESTION_ID,
          name: CUSTOM.getdataByLangKey(backendTierQuestion?.modifierGroupLocales, "", "name"),
          type: "single",
          min: 1,
          max: 1,
          envGroupId: Number(backendTierQuestion.id),
          nextQuestionId: Number(backendTierQuestion.nextQuestionId) || null,
          options: (backendTierQuestion.modifierGroupItems || []).map((item) => {
                        const nextSizeQuestion = backendQuestions.find(
              (q) => Number(q?.id) === Number(item?.nextQuestionId)
            );
            const optionName = CUSTOM.getdataByLangKey(item?.modifierGroupItemLocales, "", "name") || "";
const tierCount = getLayerTierCount(optionName);
            return {
              id: Number(item?.id),
              name: CUSTOM.getdataByLangKey(item?.modifierGroupItemLocales, "", "name") || "",
              tierCount,
              price: Number(item?.price) || 0,
              envItemId: Number(item?.id),
              nextQuestionId: Number(item?.nextQuestionId) ||
                Number(backendTierQuestion.nextQuestionId) || null,
              image: getShapeCakeImage(selectedShapeName, tierCount),
            };
          }),
        }
      : multiLayerQuestion;
    const selectedTierId = selectedData.find(
      (group) => group.group_id === MULTI_LAYER_QUESTION_ID
    )?.items?.[0];
    const selectedTierOption = getSafeOptions(tierQuestion).find(
      (option) => option?.id === selectedTierId
    );
    const effectiveTierCount = selectedTierOption?.tierCount || 1;
    const fallbackShapeSizeQuestion = getSizeQuestionForShapeAndTier(
      backendQuestions,
      selectedShapeName,
      effectiveTierCount
    );
            const sizeQuestionId = selectedTierOption?.nextQuestionId ||
      fallbackShapeSizeQuestion?.id || tierQuestion?.nextQuestionId ||
      CUSTOM_CAKE_IDS.SIZE_GROUP_ID;
    const backendSizeQuestion =
      backendQuestions.find(
        (question) => Number(question?.id) === Number(sizeQuestionId)
      ) ||
      fallbackShapeSizeQuestion ||
      backendQuestions.find(
        (question) => Number(question?.id) === CUSTOM_CAKE_IDS.SIZE_GROUP_ID
      );
    const backendSizeOptions = sortSizeOptions(
      (backendSizeQuestion?.modifierGroupItems || [])
        .map((item) => {
          const name = CUSTOM.getdataByLangKey(
            item?.modifierGroupItemLocales,
            "",
            "name"
          );
          const sizeInches = getCakeSizeInches(name);
          if (sizeInches === null) return null;

          return {
            id: Number(item?.id),
            name,
            price: Number(item?.price) || 0,
            subPrice: Number(item?.subPrice ?? item?.price) || 0,
            image: getShapeCakeImage(selectedShapeName, 1, true),
            sizeInches,
            envItemId: Number(item?.id),
          };
        })
        .filter(Boolean)
    );
    const questionsWithBackendOptions = STATIC_QUESTIONS.map((question) => {
      if (question.id === 2) {
        return {
          ...question,
          options: shapeOptions,
          nextQuestionId: Number(backendShapeQuestion?.nextQuestionId) || null,
        };
      }

      if (question.id === 1 && backendSizeOptions.length) {
        return {
          ...question,
          envGroupId: Number(backendSizeQuestion?.id) || question.envGroupId,
          nextQuestionId: Number(backendSizeQuestion?.nextQuestionId) || null,
                    min: Number(backendSizeQuestion?.min) || 1,
          max: Number(backendSizeQuestion?.max) || backendSizeOptions.length,
          options: backendSizeOptions.map((option) => {
            const backendItem = backendSizeQuestion.modifierGroupItems.find(
              (item) => Number(item?.id) === option.id
            );
            return {
              ...option,
              nextQuestionId: Number(backendItem?.nextQuestionId) ||
                Number(backendSizeQuestion.nextQuestionId) || null,
            };
          }),
        };
      }

      const backendQuestion = backendQuestions.find(
        (item) => Number(item?.id) === Number(question.envGroupId)
      );
      if (!backendQuestion) return question;

            return {
        ...question,
        nextQuestionId: Number(backendQuestion.nextQuestionId) || null,
        min: Number(backendQuestion.min) || 0,
        order: Number(backendQuestion.order) || 999,
        maxQuantity: Math.max((backendQuestion.modifierGroupItems || []).length - 1, 0),
        options: question.options.map((option) => {
          const backendItem = (backendQuestion.modifierGroupItems || []).find(
            (item) => Number(item?.id) === Number(option.envItemId)
          );
          return {
            ...option,
            price: backendItem ? Number(backendItem.price) || 0 : option.price,
            nextQuestionId: Number(backendItem?.nextQuestionId) ||
              Number(backendQuestion.nextQuestionId) || null,
          };
        }),
      };
    });

    const shapeQuestion = questionsWithBackendOptions.find((question) => question.id === 2);
    const sizeQuestion = questionsWithBackendOptions.find((question) => question.id === 1);
    const remainingQuestions = questionsWithBackendOptions.filter(
      (question) => question.id !== 1 && question.id !== 2
    );
    const questionByBackendId = new Map(
      [...remainingQuestions, sizeQuestion, tierQuestion, shapeQuestion]
        .filter(Boolean)
        .map((question) => [Number(question.envGroupId), question])
    );
    const orderedRemainingQuestions = [];
    const seenQuestionIds = new Set([Number(shapeQuestion?.envGroupId), Number(tierQuestion?.envGroupId), Number(sizeQuestion?.envGroupId)]);
    const selectedSizeId = selectedData.find((group) => group.group_id === 1)?.items?.[0];
    const selectedSizeOption = getSafeOptions(sizeQuestion).find((option) => option?.id === selectedSizeId);
    let nextQuestionId = selectedSizeOption?.nextQuestionId || sizeQuestion?.nextQuestionId;

    while (nextQuestionId && !seenQuestionIds.has(Number(nextQuestionId))) {
      const nextQuestion = questionByBackendId.get(Number(nextQuestionId));
      if (!nextQuestion) break;
      orderedRemainingQuestions.push(nextQuestion);
      seenQuestionIds.add(Number(nextQuestionId));
      const selectedOptionId = selectedData.find(
        (group) => group.group_id === nextQuestion.id
      )?.items?.[0];
      const selectedOption = getSafeOptions(nextQuestion).find(
        (option) => option?.id === selectedOptionId
      );
      nextQuestionId = selectedOption?.nextQuestionId || nextQuestion.nextQuestionId;
    }

    const remainingUnroutedQuestions = remainingQuestions.filter(
      (question) => !seenQuestionIds.has(Number(question.envGroupId))
    );

    return [
      shapeQuestion,
      tierQuestion,
      sizeQuestion,
      ...orderedRemainingQuestions,
      ...remainingUnroutedQuestions,
    ].filter(Boolean);
  }, [backendQuestions, multiLayerQuestion, selectedData, selectedShape]);

  const stepLabels = [
    t("stepLabel_reference"),
    ...wizardQuestions.map((question) => t(STEP_CONFIG[question.id]?.label || question.name)),
  ];

  // Get step icon image
  const getStepIcon = (index) => {
    const questionId = wizardQuestions[index - 1]?.id;
    const iconNumber = index === 0 ? 1 : STEP_CONFIG[questionId]?.icon;
    return `/images/step${iconNumber || index + 1}.png`;
  };

  // Get current step name
  const getCurrentStepName = () => {
    if (activeId === -1) return t("stepLabel_reference");
    if (activeId >= 0 && activeId < wizardQuestions.length) {
      return stepLabels[activeId + 1] || wizardQuestions[activeId].name;
    }
    return "";
  };

  const getCurrentStepDisplay = () => {
    const currentStep = activeId + 2;
    return `${t("step")} ${currentStep} ${t("stepOf")} ${stepLabels.length}`;
  };

  const getQuestionList = async () => {
    try {
      dispatch(changeLoader(true));
      let response = await globalRequest(
        `${API.CUSTOMCAKE.CUSTOM_CAKE_QUESTIONS_LIST}`,
        "get",
        {},
        {},
        true
      );
      response = response?.data;
      dispatch(changeLoader(false));

      if (response?.status == "SUCCESS") {
        const backendData = response?.data;
        setBackendQuestions(backendData);
        setSettingData(response?.setting_data);
        const multiLayerData = backendData.find(
          (question) => Number(question?.id) === MULTI_LAYER_BACKEND_GROUP_ID
        );
        if (multiLayerData) {
          setMultiLayerQuestion({
            id: MULTI_LAYER_QUESTION_ID,
            name: multiLayerData?.modifierGroupLocales?.[0]?.name,
            type: "single",
            min: 1,
            max: 1,
            envGroupId: MULTI_LAYER_BACKEND_GROUP_ID,
            nextQuestionId: Number(multiLayerData.nextQuestionId) || null,
            options: (multiLayerData?.modifierGroupItems || []).map((item) => {
                            const nextSizeQuestion = backendData.find(
                (q) => Number(q?.id) === Number(item?.nextQuestionId)
              );
              const optionName = CUSTOM.getdataByLangKey(item?.modifierGroupItemLocales, "", "name") || "";
const tierCount = getLayerTierCount(optionName);
              return {
                id: Number(item?.id),
                name: CUSTOM.getdataByLangKey(
                  item?.modifierGroupItemLocales,
                  "",
                  "name"
                ) || "",
                tierCount,
                price: Number(item?.price) || 0,
                envItemId: Number(item?.id),
                nextQuestionId: Number(item?.nextQuestionId) ||
                  Number(multiLayerData.nextQuestionId) || null,
                image: getShapeCakeImage(selectedShape, tierCount),
              };
            }),
          });
        } else {
          setMultiLayerQuestion(null);
        }
        /*
        const mapping = {};
        backendData.forEach((backendQuestion, index) => {
          const questionName = CUSTOM.getdataByLangKey(
            backendQuestion.modifierGroupLocales,
            "",
            "name"
          );
          const staticQuestion = STATIC_QUESTIONS[index];
          if (staticQuestion) {
            mapping[staticQuestion.id] = {
              backendGroupId: backendQuestion.id,
              options: {}
            };
            
            backendQuestion.modifierGroupItems.forEach((backendItem, optIndex) => {
              const optionName = CUSTOM.getdataByLangKey(
                backendItem.modifierGroupItemLocales,
                "",
                "name"
              );
              
              const staticOption = staticQuestion.options[optIndex];
              
              if (staticOption) {
                mapping[staticQuestion.id].options[staticOption.id] = backendItem.id;
              } else {
                console.warn(`    No static option found for index ${optIndex}`);
              }
            });
          }
        });
        
        setIdMapping(mapping);
        */

        setIdMapping({ envBased: true });
      } else {
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "error",
          })
        );
      }
    } catch (e) {
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: e?.message,
          snackbarState: "error",
        })
      );
    }
    setApiStatus(true);
  };

  const getSelectedShapeName = () => {
    if (selectedShape) return selectedShape;
    const selectedShapeId = selectedData.find((group) => group.group_id === 2)?.items?.[0];
    const shapeQuestion = wizardQuestions.find((q) => q.id === 2);
    const shapeOption = getSafeOptions(shapeQuestion).find((opt) => opt?.id === selectedShapeId);
    return shapeOption?.name || "Round";
  };

  const getSelectedTierLevel = () => {
    const sizeItems = selectedData.find((group) => group.group_id === 1)?.items || [];
    if (sizeItems.length >= 3) {
      return 3;
    }

    const selectedLayer = selectedData.find(
      (group) => group.group_id === MULTI_LAYER_QUESTION_ID
    )?.items?.[0];
    const layerQuestion = wizardQuestions.find(
      (question) => question.id === MULTI_LAYER_QUESTION_ID
    );
    const layerOption = getSafeOptions(layerQuestion).find(
      (option) => option?.id === selectedLayer
    );
    if (layerOption?.tierCount && layerOption.tierCount > 1) {
      return Math.max(layerOption.tierCount, sizeItems.length > 1 ? Math.min(sizeItems.length, 3) : 1);
    }
    if (layerOption?.name) {
      const tc = getLayerTierCount(layerOption.name);
      if (tc > 1) return Math.max(tc, sizeItems.length > 1 ? Math.min(sizeItems.length, 3) : 1);
    }

    if (sizeItems.length > 1) {
      return Math.min(sizeItems.length, 3);
    }

    return 1;
  };

  const getOptionImage = (option, questionId) => {
    const shapeName = getSelectedShapeName();
    const tierLevel = getSelectedTierLevel();

    // 1. Size step (questionId === 1)
    if (questionId === 1) {
      return getShapeCakeImage(shapeName, tierLevel, true);
    }

    // 2. Shape step (questionId === 2)
    if (questionId === 2) {
      const optShape = option?.shapeKey || option?.name || shapeName;
      return getShapeCakeImage(optShape, 1, false);
    }

    // 3. Multi-layer step (questionId === MULTI_LAYER_QUESTION_ID)
    if (questionId === MULTI_LAYER_QUESTION_ID) {
      const shapeTierCount = option?.tierCount || 1;
      return getShapeCakeImage(shapeName, shapeTierCount, false);
    }

    // 4. Flavor step (questionId === 3)
    if (questionId === 3) {
      return getFlavorCakeImage(option, shapeName, tierLevel);
    }

    // 5. 3D pieces step (questionId === 4)
    if (questionId === 4) {
      const qty = getQuantity(4, option.id);
      return get3DPiecesCakeImage(qty, shapeName, tierLevel);
    }

    // 6. Glitter step (questionId === 5)
    if (questionId === 5) {
      const qty3D = getQuantity(4, 41);
      return getGlitterCakeImage(option, shapeName, tierLevel, qty3D);
    }

    // 7. Ribbon step (questionId === 6)
    if (questionId === 6) {
      const qty = getQuantity(6, option.id);
      const glitterOptionId = selectedData.find((group) => group.group_id === 5)?.items?.[0];
      const isSilver = glitterOptionId === 52 || glitterOptionId === CUSTOM_CAKE_IDS.GLITTER_SILVER_ID;
      return getRibbonCakeImage(qty, shapeName, tierLevel, isSilver);
    }

    // 8. Message step (questionId === 7)
    if (questionId === 7) {
      return option?.image || "/images/specialGift.png";
    }

    if (option?.image) return option.image;
    return getShapeCakeImage(shapeName, tierLevel, false);
  };

  // Get preview image
  const getPreviewImage = () => {
    const shapeName = getSelectedShapeName();
    const tierLevel = getSelectedTierLevel();

    const isReached = (questionId) => {
      const index = wizardQuestions.findIndex((q) => q?.id === questionId);
      return index !== -1 && index <= activeId;
    };

    const qty3D = isReached(4) ? getQuantity(4, 41) : 0;
    const ribbonQty = isReached(6) ? getQuantity(6, 61) : 0;
    const glitterGroup = isReached(5)
      ? selectedData.find((group) => group.group_id === 5)
      : null;
    const glitterOptionId = glitterGroup?.items?.[0];
    const flavorGroup = isReached(3)
      ? selectedData.find((group) => group.group_id === 3)
      : null;

    const isSilver =
      glitterOptionId === 52 ||
      glitterOptionId === CUSTOM_CAKE_IDS.GLITTER_SILVER_ID;

    if (ribbonQty > 0) {
      return getRibbonCakeImage(ribbonQty, shapeName, tierLevel, isSilver);
    }

      if (glitterOptionId) {
        const glitterQuestion = wizardQuestions.find((q) => q.id === 5);
        const selectedGlitter = getSafeOptions(glitterQuestion).find(
          (opt) => opt?.id === glitterOptionId
        );
        return getGlitterCakeImage(
          selectedGlitter || { id: glitterOptionId },
          shapeName,
          tierLevel,
          qty3D
        );
      }

      if (qty3D > 0) {
        return get3DPiecesCakeImage(qty3D, shapeName, tierLevel);
    }

    // 4. Flavor selected -> flavor image
    if (flavorGroup?.items?.length > 0) {
      const flavorQuestion = wizardQuestions.find((q) => q.id === 3);
      const selectedFlavor = getSafeOptions(flavorQuestion).find(
        (opt) => opt?.id === flavorGroup.items[0]
      );
      return getFlavorCakeImage(selectedFlavor || { id: flavorGroup.items[0] }, shapeName, tierLevel);
    }

    return getShapeCakeImage(shapeName, tierLevel, false);
  };

  // Check if message card has content
  const hasMessageCard = () => {
    return messageCardText.trim().length > 0;
  };

  const isOptionSelectedForStep = (stepId) => {
    if (wizardQuestions[stepId]?.id === 4) {
      return Object.keys(quantities).some(key => {
        const [qId] = key.split('_').map(Number);
        return qId === 4 && quantities[key] > 0;
      });
    }
    if (wizardQuestions[stepId]?.id === 5) {
      const glitterGroup = selectedData.find(group => group.group_id === 5);
      return !!(glitterGroup && glitterGroup.items && glitterGroup.items.length > 0);
    }
    if (wizardQuestions[stepId]?.id === 6) {
      return Object.keys(quantities).some(key => {
        const [qId] = key.split('_').map(Number);
        return qId === 6 && quantities[key] > 0;
      });
    }
    return false;
  };

  // Handle quantity increment
  const handleQuantityIncrement = (questionId, optionId) => {
    const key = `${questionId}_${optionId}`;
    const currentQty = quantities[key] || 0;
    setQuantities({
      ...quantities,
      [key]: currentQty + 1
    });
  };

  // Handle quantity decrement
  const handleQuantityDecrement = (questionId, optionId) => {
    const key = `${questionId}_${optionId}`;
    const currentQty = quantities[key] || 0;
    if (currentQty > 0) {
      setQuantities({
        ...quantities,
        [key]: currentQty - 1
      });
    }
  };

  const getQuantity = (questionId, optionId) => {
    const key = `${questionId}_${optionId}`;
    return quantities[key] || 0;
  };

  const getSelectedLayerCount = () => {
    const selectedLayer = selectedData.find(
      (group) => group.group_id === MULTI_LAYER_QUESTION_ID
    )?.items?.[0];
    const layerQuestion = wizardQuestions.find(
      (question) => question.id === MULTI_LAYER_QUESTION_ID
    );
    const layerOption = getSafeOptions(layerQuestion).find(
      (option) => option?.id === selectedLayer
    );
    return layerOption?.tierCount || 1;
  };

  const getSizeLimits = () => {
    const sizeQuestion = wizardQuestions.find((q) => q.id === 1);
    const configuredMinLayers = Number(sizeQuestion?.min);
    const configuredMaxLayers = Number(sizeQuestion?.max);
    const selectedLayerCount = getSelectedLayerCount();
    const maxLayers =
      configuredMaxLayers > 0
        ? configuredMaxLayers
        : selectedLayerCount;
    return {
      minLayers: Math.min(
        configuredMinLayers > 0 ? configuredMinLayers : selectedLayerCount,
        maxLayers
      ),
      maxLayers,
    };
  };

  const isSizeOptionDisabled = (option) => {
    if (sizeTierIndex === 0) return false;
    const selected = selectedData.find((g) => g.group_id === 1)?.items || [];
    const previousTierOption = getSafeOptions(
      wizardQuestions.find((q) => q.id === 1)
    ).find((o) => o?.id === selected[sizeTierIndex - 1]);

    return Boolean(
      previousTierOption &&
        Number(option?.sizeInches) >= Number(previousTierOption?.sizeInches)
    );
  };

  const handleSizeOptionSelect = (optionId) => {
    const isMulti = getSelectedLayerCount() > 1;
    const sizeOptions = getSafeOptions(wizardQuestions.find((q) => q.id === 1));

    setSelectedData((previousData) => {
      const sizeGroup = previousData.find((g) => g.group_id === 1);
      const current = sizeGroup?.items || [];
      let items;

      if (!isMulti) {
        items = [optionId];
      } else {
        if (current[sizeTierIndex] === optionId) return previousData;

        const option = sizeOptions.find((o) => o?.id === optionId);
        const previousTierOption =
          sizeTierIndex > 0
            ? sizeOptions.find((o) => o?.id === current[sizeTierIndex - 1])
            : null;

        if (
          !option ||
          (previousTierOption &&
            Number(option.sizeInches) >= Number(previousTierOption.sizeInches))
        ) {
          return previousData;
        }

        items = [...current.slice(0, sizeTierIndex), optionId];
      }

      const updated = { group_id: 1, items };
      return sizeGroup
        ? previousData.map((g) => (g.group_id === 1 ? updated : g))
        : [...previousData, updated];
    });
  };
  const getTotalPrice = () => {
    let total = 0;

    total += selectedData.reduce((sum, groupData) => {
      const question = wizardQuestions.find(q => q.id === groupData.group_id);
      if (question) {
        groupData.items.forEach((itemId, index) => {
          const option = getSafeOptions(question).find(opt => opt?.id === itemId);
          if (option) {
            sum += groupData.group_id === 1 && index > 0
              ? option.subPrice ?? option.price
              : option.price;
          }
        });
      }
      return sum;
    }, 0);

    Object.keys(quantities).forEach(key => {
      const [questionId, optionId] = key.split('_').map(Number);
      const qty = quantities[key];

      if (qty > 0) {
        const question = wizardQuestions.find(q => q.id === questionId);
        if (question) {
          const option = getSafeOptions(question).find(opt => opt?.id === optionId);
          if (option) {
            total += option.price * qty;
          }
        }
      }
    });

    if (hasMessageCard()) {
      const messageQuestion = wizardQuestions.find(q => q.id === 7);
      if (messageQuestion && messageQuestion.options[0]) {
        total += messageQuestion.options[0].price;
      }
    }

    return parseFloat(total.toFixed(2));
  };

  // Save to localStorage
  const saveToLocalStorage = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.SELECTED_DATA, JSON.stringify(selectedData));
      localStorage.setItem(STORAGE_KEYS.IMAGES, JSON.stringify(allSelectedImages));
      localStorage.setItem(STORAGE_KEYS.REFERENCE, reference);
      localStorage.setItem(STORAGE_KEYS.TEXT, textOnCake);
      localStorage.setItem(STORAGE_KEYS.ACTIVE_STEP, activeId.toString());
      localStorage.setItem(STORAGE_KEYS.COMPLETED_STEPS, JSON.stringify(completedSteps));
      localStorage.setItem(STORAGE_KEYS.TOTAL_PRICE, totalPrice.toString());
      localStorage.setItem(STORAGE_KEYS.SIZE_TIER_INDEX, sizeTierIndex.toString());
      localStorage.setItem('custom_cake_quantities', JSON.stringify(quantities));
      localStorage.setItem('custom_cake_message', messageCardText);
      if (selectedShape) {
        localStorage.setItem(STORAGE_KEYS.SELECTED_SHAPE, selectedShape);
      }
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  };

  // Load from localStorage
  const loadFromLocalStorage = () => {
    try {
      const savedSelectedData = localStorage.getItem(STORAGE_KEYS.SELECTED_DATA);
      const savedImages = localStorage.getItem(STORAGE_KEYS.IMAGES);
      const savedReference = localStorage.getItem(STORAGE_KEYS.REFERENCE);
      const savedText = localStorage.getItem(STORAGE_KEYS.TEXT);
      const savedStep = localStorage.getItem(STORAGE_KEYS.ACTIVE_STEP);
      const savedCompleted = localStorage.getItem(STORAGE_KEYS.COMPLETED_STEPS);
      const savedPrice = localStorage.getItem(STORAGE_KEYS.TOTAL_PRICE);
      const savedShape = localStorage.getItem(STORAGE_KEYS.SELECTED_SHAPE);
      const savedSizeTierIndex = localStorage.getItem(STORAGE_KEYS.SIZE_TIER_INDEX);
      const savedQuantities = localStorage.getItem('custom_cake_quantities');
      const savedMessage = localStorage.getItem('custom_cake_message');

      if (savedSelectedData) setSelectedData(JSON.parse(savedSelectedData));
      if (savedImages) setAllSelectedImages(JSON.parse(savedImages));
      if (savedReference) setReference(savedReference);
      if (savedText) setTextOnCake(savedText);
      if (savedStep) setActiveId(parseInt(savedStep));
      if (savedCompleted) setCompletedSteps(JSON.parse(savedCompleted));
      if (savedPrice) setTotalPrice(parseFloat(savedPrice));
      if (savedShape) setSelectedShape(savedShape);
      if (savedSizeTierIndex) setSizeTierIndex(parseInt(savedSizeTierIndex, 10));
      if (savedQuantities) setQuantities(JSON.parse(savedQuantities));
      if (savedMessage) setMessageCardText(savedMessage);
    } catch (e) {
      console.error('Error loading from localStorage:', e);
    }
  };

  // Add cart items state
  const addCartItemsState = async (question, optionId) => {
    if (question.id === 2) {
      if (checkedCheckbox(question, optionId)) return;
      const selectedOption = getSafeOptions(question).find((option) => option?.id === optionId);
      setSelectedShape(selectedOption?.name || null);
      setSelectedData((previousData) => [
        ...previousData.filter(
          (group) => ![2, MULTI_LAYER_QUESTION_ID, 1].includes(group.group_id)
        ),
        { group_id: 2, items: [optionId] },
      ]);
      setSizeTierIndex(0);
      return;
    }

    if (question.id === MULTI_LAYER_QUESTION_ID) {
      if (checkedCheckbox(question, optionId)) return;
      setSelectedData((previousData) => [
        ...previousData.filter(
          (group) =>
            group.group_id !== MULTI_LAYER_QUESTION_ID && group.group_id !== 1
        ),
        { group_id: MULTI_LAYER_QUESTION_ID, items: [optionId] },
      ]);
      setSizeTierIndex(0);
      return;
    }

    let existingModifierIndex = selectedData?.findIndex(
      (modifier) => modifier?.group_id === question?.id
    );
    let updatedSelectedData = [];

    // Track shape selection
    if (question.id === 2) {
      const option = getSafeOptions(question).find(opt => opt?.id === optionId);
      if (option && option.name) {
        setSelectedShape(option.name);
      }
    }

    if (existingModifierIndex === -1) {
      const newModifier = {
        group_id: question?.id,
        items: [optionId],
      };
      await setSelectedData((prevSelectedData) => {
        const newSelectedData = [...prevSelectedData, newModifier];
        updatedSelectedData = newSelectedData;
        existingModifierIndex = 0;
        return newSelectedData;
      });
    } else {
      if (checkedCheckbox(question, optionId) == false) {
        let max = question?.max;
        if (question?.type == "single") {
          max = 1;
        }
        let dataGetLength = selectedData[existingModifierIndex]?.items.length;
        let removeLastItem = false;
        if (dataGetLength == max && max != 0) {
          removeLastItem = true;
        }
        let newElement = optionId;
        const updatedData = selectedData.map((item) => {
          if (item.group_id === question?.id) {
            if (removeLastItem) {
              let itemArray = [...item.items];
              itemArray.pop();
              itemArray.push(newElement);
              return {
                ...item,
                items: itemArray,
              };
            } else {
              return {
                ...item,
                items: [...item.items, newElement],
              };
            }
          }
          return item;
        });
        updatedSelectedData = updatedData;
        setSelectedData(updatedData);
      } else {
        let elementToRemove = optionId;
        const updatedData = selectedData.map((item) => {
          if (item.group_id === question?.id) {
            return {
              ...item,
              items: item.items.filter((item) => item !== elementToRemove),
            };
          }
          return item;
        });
        updatedSelectedData = updatedData;
        setSelectedData(updatedData);
      }
    }
  };

  const checkedCheckbox = (question, optionId) => {
    const existingModifierIndex = selectedData?.findIndex(
      (modifier) => modifier?.group_id === question?.id
    );
    if (existingModifierIndex !== -1) {
      let dataGetLength = selectedData[existingModifierIndex]?.items;
      if (dataGetLength.includes(optionId)) {
        return true;
      }
    }
    return false;
  };

  const isCurrentStepValid = () => {
    if (activeId === -1) {
      return (allSelectedImages.length > 0 || reference) &&
        (!reference || CUSTOM.isValidField(reference, "url"));
    }

    if (activeId >= 0 && activeId < wizardQuestions.length) {
      const currentQuestion = wizardQuestions[activeId];
      let min = currentQuestion?.min;
      if (currentQuestion?.type == "single" && currentQuestion?.min > 0) {
        min = 1;
      }

      if (currentQuestion?.type == "message") {
        return true;
      }

      if (currentQuestion?.id === 1 && getSelectedLayerCount() > 1) {
        const selectedSizes =
          selectedData.find((group) => group.group_id === 1)?.items || [];
        const { minLayers } = getSizeLimits();
        return Boolean(
          selectedSizes[sizeTierIndex] ||
            (sizeTierIndex >= minLayers &&
              selectedSizes.length >= minLayers)
        );
      }

      let existingModifierIndex = selectedData?.findIndex(
        (modifier) => modifier?.group_id === currentQuestion?.id
      );

      let totalItems = 0;
      if (existingModifierIndex !== -1) {
        totalItems = selectedData[existingModifierIndex].items.length;
      }

      if (currentQuestion?.type == "quantity") {
        Object.keys(quantities).forEach(key => {
          const [questionId] = key.split('_').map(Number);
          if (questionId === currentQuestion.id) {
            totalItems += quantities[key];
          }
        });
      }

      return totalItems >= min;
    }

    return true;
  };

  const handleNextStep = () => {
    if (!isCurrentStepValid()) {
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: t("pleaseCompleteCurrentStep"),
          snackbarState: "error",
        })
      );
      return;
    }

    const currentQuestion = wizardQuestions[activeId];
    if (currentQuestion?.id === 1 && getSelectedLayerCount() > 1) {
      const selectedSizes =
        selectedData.find((group) => group.group_id === 1)?.items || [];
      const { minLayers, maxLayers } = getSizeLimits();
      const sizeOptions = getSafeOptions(currentQuestion);
      const currentSize = sizeOptions.find(
        (option) => option?.id === selectedSizes[sizeTierIndex]
      );
      const hasSmallerSize = sizeOptions.some(
        (option) =>
          !selectedSizes.includes(option?.id) &&
          Number(option?.sizeInches) < Number(currentSize?.sizeInches)
      );

      if (sizeTierIndex < minLayers - 1) {
        if (!hasSmallerSize) {
          dispatch(
            setSnackbar({
              snackbarOpen: true,
              snackbarMessage: t(
                selectedSizes.length === 1
                  ? "pleaseSelectAtLeastTwoLayers"
                  : "pleaseSelectSmallerLayer"
              ),
              snackbarState: "error",
            })
          );
          return;
        }

        setSizeTierIndex(sizeTierIndex + 1);
        return;
      }

      if (
        sizeTierIndex < maxLayers - 1 &&
        selectedSizes[sizeTierIndex] &&
        hasSmallerSize
      ) {
        setSizeTierIndex(sizeTierIndex + 1);
        return;
      }

      if (selectedSizes.length < minLayers) {
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: t("pleaseSelectAtLeastTwoLayers"),
            snackbarState: "error",
          })
        );
        return;
      }
    }

    if (!completedSteps.includes(activeId)) {
      setCompletedSteps([...completedSteps, activeId]);
    }

    if (activeId < wizardQuestions.length - 1) {
      setActiveId(activeId + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (activeId === wizardQuestions.length - 1) {
      handleCompleteOrder();
    }
  };

  const handlePreviousStep = () => {
    if (activeId > -1) {
      const currentQuestion = wizardQuestions[activeId];
      if (
        currentQuestion?.id === 1 &&
        getSelectedLayerCount() > 1 &&
        sizeTierIndex > 0
      ) {
        setSizeTierIndex(sizeTierIndex - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const previousQuestion = wizardQuestions[activeId - 1];
      if (previousQuestion?.id === 1 && getSelectedLayerCount() > 1) {
        const selectedSizes =
          selectedData.find((group) => group.group_id === 1)?.items || [];
        if (selectedSizes.length > 0) {
          setSizeTierIndex(selectedSizes.length - 1);
        }
      }

      setActiveId(activeId - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleImageChange = (e) => {
    e.preventDefault();
    let file = e.target.files[0];
    let imageError = CUSTOM.checkFileExtention(file.name, "png|jpg|jpeg", "", "");
    if (imageError) {
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: imageError,
          snackbarState: "error",
        })
      );
      document.getElementById("image-upload-field").value = "";
      return false;
    }
    let reader = new FileReader();
    reader.onloadend = () => {
      getCompressedImage(reader.result);
    };
    if (file) {
      reader.readAsDataURL(file);
    }
    document.getElementById("image-upload-field").value = "";
  };

  const getCompressedImage = (base64Url) => {
    const outputQuality = 0.9;
    const maxSizeInBytes = 0.7 * 1024 * 1024;

    const img = new Image();
    img.src = base64Url;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      const tryCompress = (quality) => {
        return new Promise((resolve) => {
          canvas.toBlob(
            (blob) => {
              if (blob.size <= maxSizeInBytes || quality <= 0.1) {
                resolve(blob);
              } else {
                tryCompress(quality - 0.1).then(resolve);
              }
            },
            "image/jpeg",
            quality
          );
        });
      };

      tryCompress(outputQuality).then((blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          uploadImageToServer(reader.result);
        };
        reader.readAsDataURL(blob);
      });
    };
  };

  const uploadImageToServer = async (base64Image) => {
    try {
      dispatch(changeLoader(true));
      let response = await globalRequest(
        `${API.CUSTOMCAKE.UPLOAD_IMAGE}`,
        "post",
        { image: base64Image },
        {},
        true
      );
      response = response?.data;
      dispatch(changeLoader(false));

      if (response?.status == "SUCCESS") {
        const updatedImages = [...allSelectedImages, response?.data];
        setAllSelectedImages(updatedImages);
        setImageForCrop("");
      } else {
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "error",
          })
        );
      }
    } catch (e) {
      dispatch(changeLoader(false));
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: e?.message,
          snackbarState: "error",
        })
      );
    }
  };

  const getInputData = () => {
    const cake_details = [];


    selectedData.forEach(group => {
      const staticQuestion = wizardQuestions.find(q => q.id === group.group_id);
      if (!staticQuestion || !staticQuestion.envGroupId) {
        console.warn(`No envGroupId for static question ${group.group_id}`);
        return;
      }

      const backendItems = group.items.map(staticItemId => {
        const option = getSafeOptions(staticQuestion).find(opt => opt?.id === staticItemId);
        if (!option || !option.envItemId) {
          console.warn(`No envItemId for option ${staticItemId} in question ${group.group_id}`);
          return null;
        }
        return option.envItemId;
      }).filter(id => id !== null && id !== undefined && !isNaN(id));

      if (backendItems.length > 0) {
        cake_details.push({
          group_id: staticQuestion.envGroupId,
          items: backendItems
        });
      }
    });

    Object.keys(quantities).forEach(key => {
      const [staticQuestionId, staticOptionId] = key.split('_').map(Number);
      const qty = quantities[key];

      if (qty > 0) {
        const staticQuestion = wizardQuestions.find(q => q.id === staticQuestionId);
        if (!staticQuestion || !staticQuestion.envGroupId) {
          console.warn(`No envGroupId for quantity question ${staticQuestionId}`);
          return;
        }

        let backendItemId = null;

        if (staticQuestion.envQuantityMap && staticQuestion.envQuantityMap[qty] !== undefined) {
          backendItemId = staticQuestion.envQuantityMap[qty];
        } else {
          const option = getSafeOptions(staticQuestion).find(opt => opt?.id === staticOptionId);
          if (option && option.envItemId) {
            backendItemId = option.envItemId;
          }
        }

        if (backendItemId && !isNaN(backendItemId)) {
          const existingGroupIndex = cake_details.findIndex(
            g => g.group_id === staticQuestion.envGroupId
          );

          if (existingGroupIndex !== -1) {
            cake_details[existingGroupIndex].items = [backendItemId];
          } else {
            cake_details.push({
              group_id: staticQuestion.envGroupId,
              items: [backendItemId]
            });
          }
        } else {
          console.warn(`No backendItemId found for qty ${qty} in question ${staticQuestionId}`);
        }
      }
    });

    if (hasMessageCard()) {
      const messageQuestion = wizardQuestions.find(q => q.id === 7);
      if (
        messageQuestion &&
        messageQuestion.envGroupId &&
        messageQuestion.options[0]?.envItemId &&
        !isNaN(messageQuestion.options[0].envItemId)
      ) {
        cake_details.push({
          group_id: messageQuestion.envGroupId,
          items: [messageQuestion.options[0].envItemId]
        });
      }
    }

    return {
      cake_details: cake_details,
      upload_image_links: allSelectedImages,
      image_link: reference,
      text_on_cake: textOnCake,
      message_card_text: messageCardText,
      id: allInputData?.id ? allInputData?.id : "",
    };
  };

  const processCustomization = (cakeDetails) => {
    try {
      const customizations = [];

      cakeDetails.forEach((group) => {
        const question = group?.modifierGroupLocales?.[0]?.name;
        group?.modifierGroupItems?.forEach((item) => {
          const answer = item?.modifierGroupItemLocales?.[0]?.name;
          if (question && answer) {
            customizations.push({ question, answer });
          }
        });
      });

      return customizations;
    } catch (error) {
      return [];
    }
  };

  // Save for later function
  const saveForLater = async () => {
    try {
      dispatch(changeLoader(true));

      const inputData = getInputData();

      if (!inputData || !inputData.cake_details || inputData.cake_details.length === 0) {
        dispatch(changeLoader(false));
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: t("unableToSavePleaseEnsure"),
            snackbarState: "error",
          })
        );
        return;
      }

      let response = await globalRequest(
        API?.CUSTOMCAKE?.SAVE_FOR_LATTER,
        "post",
        {
          cake_details: inputData.cake_details,
          upload_image_links: inputData.upload_image_links,
          image_link: inputData.image_link,
          text_on_cake: inputData.text_on_cake,
          message_card_text: inputData.message_card_text,
          id: inputData.id,
          gaData: CUSTOM.getGACookieValues()
        },
        {},
        true
      );
      response = response?.data;
      dispatch(changeLoader(false));

      if (response?.status == "SUCCESS") {
        const customization = processCustomization(response?.data?.cakeDetails);
        CUSTOM.trackCustomCakeSave({ cake_type: "Custom", customization: customization });

        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "success",
          })
        );

        addDeleteGetLocalStorage(
          storageKeys.ORDER_TAB,
          "mycustomcake",
          "add",
          "single"
        );

        clearLocalStorage();

        navigate("/my-order");
      } else {
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "error",
          })
        );
      }
    } catch (e) {
      dispatch(changeLoader(false));
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: e?.message,
          snackbarState: "error",
        })
      );
    }
  };

  const placeOrder = async () => {
    try {
      dispatch(changeLoader(true));

      const inputData = getInputData();

      if (!inputData || !inputData.cake_details || inputData.cake_details.length === 0) {
        dispatch(changeLoader(false));
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: t("unableToProcessOrderPleaseEnsure"),
            snackbarState: "error",
          })
        );
        return;
      }

      let placeOrderInput = {
        customCakeId: 0,
        device_type: "web",
        customCakeDetails: {
          cake_details: inputData.cake_details,
          upload_image_links: inputData.upload_image_links,
          image_link: inputData.image_link,
          text_on_cake: inputData.text_on_cake,
          message_card_text: inputData.message_card_text,
        },
      };


      let response = await globalRequest(
        API?.CUSTOMCAKE?.PLACE_ORDER,
        "post",
        { ...placeOrderInput, gaData: CUSTOM.getGACookieValues() },
        {},
        true
      );
      response = response?.data;
      dispatch(changeLoader(false));

      if (response?.status == "SUCCESS") {
        const customization = processCustomization(response?.cakeDetails);
        CUSTOM.trackCustomCakeCheckout({ cake_type: "Custom", customization: customization });

        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "success",
          })
        );

        localStorage.removeItem("pendingOrderData");
        clearLocalStorage();

        navigate("/my-order");
      } else {
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: response?.message,
            snackbarState: "error",
          })
        );
      }
    } catch (e) {
      dispatch(changeLoader(false));
      console.error("Place order error:", e);
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: e?.message || t("failedToPlaceOrder"),
          snackbarState: "error",
        })
      );
    }
  };

  // Clear localStorage function
  const clearLocalStorage = () => {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    localStorage.removeItem('custom_cake_quantities');
    localStorage.removeItem('custom_cake_message');
  };

  const handleCompleteOrder = () => {
    if (!idMapping || !idMapping.envBased) {
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: t("systemStillLoading"),
          snackbarState: "error",
        })
      );
      return;
    }

    const currentPage = window.location.pathname;

    const inputData = getInputData();

    if (!inputData) {
      return;
    }

    if (!auth || auth?.id === 0) {
      saveToLocalStorage();
      localStorage.setItem(STORAGE_KEYS.ACTIVE_STEP, (wizardQuestions.length - 1).toString());
      localStorage.setItem(
        "pendingOrderData",
        JSON.stringify({
          action: "placeOrder",
          page: currentPage,
        })
      );
      navigate(`/login?redirect=${encodeURIComponent(currentPage)}`);
    } else {
      placeOrder();
    }
  };

  const getSelectedItemsForPreview = () => {
    const items = [];

    selectedData.forEach((selectedGroup) => {
  const question = wizardQuestions.find((q) => q?.id === selectedGroup?.group_id);
  if (question) {
    const selectedOptions = selectedGroup.items
      .map((itemId) => getSafeOptions(question).find((opt) => opt?.id === itemId))
      .filter(Boolean);

    selectedOptions.forEach((option, index) => {
      items.push({
        groupName: t(question.name),
        name: t(option.name),
        price: selectedGroup.group_id === 1 && index > 0
          ? option.subPrice ?? option.price
          : option.price,
        quantity: 1,
      });
    });
  }
});

    Object.keys(quantities).forEach(key => {
      const [questionId, optionId] = key.split('_').map(Number);
      const qty = quantities[key];

      if (qty > 0) {
        const question = wizardQuestions.find(q => q.id === questionId);
        if (question) {
          const option = getSafeOptions(question).find(opt => opt?.id === optionId);
          if (option) {
            items.push({
              groupName: t(question.name),
              name: t(option.name),
              price: option.price,
              quantity: qty,
              isQuantityItem: true
            });
          }
        }
      }
    });

    if (hasMessageCard()) {
      const messageQuestion = wizardQuestions.find(q => q.id === 7);
      if (messageQuestion && messageQuestion.options[0]) {
        items.push({
          groupName: t(messageQuestion.name),
          name: t(messageQuestion.options[0].name),
          price: messageQuestion.options[0].price,
          quantity: 1,
          isMessage: true
        });
      }
    }

    return items;
  };

  const renderPreviewContent = () => {
    return (
      <>
        <PreviewHeader>{t("livePreview")}</PreviewHeader>
        <PreviewDescription>
          {t("livePreviewDescription")}
        </PreviewDescription>

        {wizardQuestions[activeId]?.id === 7 ? (
          <PreviewImageContainer>
            <PreviewCakeImage
              src="/images/specialGift.png"
              alt={t("messageCard")}
              onError={(e) => {
                e.target.src = '/images/specialGift.png';
              }}
              style={{ maxWidth: '350px' }}
            />
          </PreviewImageContainer>
        ) : (
          selectedData.length > 0 && (
            <PreviewImageContainer>
              <PreviewCakeImage
                src={getPreviewImage()}
                alt={t("cakePreview")}
                onError={(e) => {
                  e.target.src = '/images/roundCake.png';
                }}
              />
            </PreviewImageContainer>
          )
        )}

        {reference && (
          <>
            <PreviewDivider />
            <PreviewBox>
              <PreviewBoxTitle>{t("referenceLinkLabel")}</PreviewBoxTitle>
              <PreviewBoxValue style={{ fontSize: '13px', wordBreak: 'break-all' }}>
                {reference}
              </PreviewBoxValue>
            </PreviewBox>
          </>
        )}

        {textOnCake && (
          <PreviewBox>
            <PreviewBoxTitle>{t("nameoncake")}</PreviewBoxTitle>
            <PreviewBoxValue>{textOnCake}</PreviewBoxValue>
          </PreviewBox>
        )}

        {messageCardText && (
          <PreviewBox>
            <PreviewBoxTitle>{t("messageCard")}</PreviewBoxTitle>
            <PreviewBoxValue>{messageCardText}</PreviewBoxValue>
          </PreviewBox>
        )}

        {/* Selected Items */}
        {getSelectedItemsForPreview().map((item, index) => (
          <PreviewBox key={index}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <PreviewBoxValue>
                  {item.name}
                  {(item.quantity > 1 || item.isQuantityItem) && ` (x${item.quantity})`}
                </PreviewBoxValue>
              </div>
              <OptionPriceTag>
                <SarIcon src="/images/Groupsar2.svg" alt="SAR" />
                {item.quantity > 1 ? item.price * item.quantity : item.price}
              </OptionPriceTag>
            </div>
          </PreviewBox>
        ))}

        <TotalContainer>
          <TotalText>{t("totalAmount")}</TotalText>
          <TotalPrice>
            <SarIcon src="/images/sarRed.svg" alt="SAR" />
            {CUSTOM.setPrice ? CUSTOM.setPrice(totalPrice) : totalPrice}
          </TotalPrice>
        </TotalContainer>
      </>
    );
  };

  const renderCurrentStep = () => {
    if (activeId === -1) {
      return (
        <>
          <QuestionContent>
            <QuestionHeader>{t("uploadReferenceImages")}</QuestionHeader>

            <PinterestBanner>
              <PinterestIconWrapper>
                <PinterestImage
                  src="/images/pinterest.png"
                  alt="Pinterest"
                />
              </PinterestIconWrapper>
              <PinterestTextArea>
                <PinterestTitle>{t("browsePinterestTitle")}</PinterestTitle>
                <PinterestSubtext>{t("browsePinterestSubtext")}</PinterestSubtext>
              </PinterestTextArea>
              <BrowseBtn
                href="https://in.pinterest.com/rabbitholesa/?invite_code=399f0ea3a49348dea5272cf4467ddabf"
                target="_blank"
                rel="noreferrer"
              >
                <BrowseBtnText>{t("browseCakes")}</BrowseBtnText>
              </BrowseBtn>
            </PinterestBanner>

            {allSelectedImages.length > 0 && (
              <UploadedImagesGrid>
                {allSelectedImages.map((img, index) => (
                  <ImageItem key={index}>
                    <UploadedImg
                      src={img.includes("://") ? img : `${process.env.REACT_APP_BASEURL}${img}`}
                      alt={`${t("uploadImage")} ${index + 1}`}
                    />
                    <RemoveBtn
                      onClick={() => {
                        const updatedImages = allSelectedImages.filter((_, i) => i !== index);
                        setAllSelectedImages(updatedImages);
                      }}
                    >
                      ×
                    </RemoveBtn>
                  </ImageItem>
                ))}
              </UploadedImagesGrid>
            )}

            <FormLabel>
              {t("uploadImage")} <RequiredStar>*</RequiredStar>
            </FormLabel>
            {allSelectedImages.length < 5 && (
              <UploadZone htmlFor="image-upload-field">
                <UploadIconWrapper>
                  <UploadIconImg src="/images/uploadImage.png" alt={t("uploadImage")} />
                </UploadIconWrapper>
                <UploadMainText>{t("clickToUpload")}</UploadMainText>
                <UploadSubText>{t("uploadCakeImagesInPNGSlashJPGSlashJPEGFormatDot")}</UploadSubText>
                <input
                  type="file"
                  accept="image/jpeg, image/jpg, image/png"
                  id="image-upload-field"
                  onChange={handleImageChange}
                />
              </UploadZone>
            )}

            <FormLabel>
              {t("referenceLinkLabel")} <RequiredStar>*</RequiredStar>
            </FormLabel>
            <InputField
              type="text"
              placeholder={t("referenceLinkPlaceholder")}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />

            <FormLabel>{t("textOnCakeOptional")}</FormLabel>
            <InputField
              type="text"
              placeholder={t("textOnCakePlaceholder")}
              value={textOnCake}
              onChange={(e) => setTextOnCake(e.target.value)}
            />
          </QuestionContent>

          <NavButtonsContainer>
            <NavBtn
              $variant="secondary"
              onClick={handlePreviousStep}
              disabled={activeId === -1}
            >
              {`← ${t("back")}`}
            </NavBtn>
            <NavBtn
              onClick={handleNextStep}
              disabled={!isCurrentStepValid()}
            >
              {` ${t("nextStep")} →`}
            </NavBtn>
          </NavButtonsContainer>
        </>
      );
    }

    if (activeId >= 0 && activeId < wizardQuestions.length) {
      const currentQuestion = wizardQuestions[activeId];
      const isMultiTierSizeQuestion =
        currentQuestion?.id === 1 && getSelectedLayerCount() > 1;
      const selectedSizeIds = isMultiTierSizeQuestion
        ? selectedData.find((group) => group.group_id === 1)?.items || []
        : [];
      const sizeOptions = getSafeOptions(currentQuestion);
      const previousTierSize = sizeTierIndex > 0
        ? sizeOptions.find(
            (option) => option?.id === selectedSizeIds[sizeTierIndex - 1]
          )
        : null;
      const currentQuestionOptions = sizeOptions.filter((option) => {
        if (!isMultiTierSizeQuestion) return true;

        if (sizeTierIndex === 0) {
          const smallerSizes = new Set(
            sizeOptions
              .map((sizeOption) => Number(sizeOption?.sizeInches))
              .filter(
                (sizeInches) =>
                  Number.isFinite(sizeInches) &&
                  sizeInches < Number(option?.sizeInches)
              )
          );
          return smallerSizes.size >= getSelectedLayerCount() - 1;
        }

        return (
          previousTierSize &&
          Number(option?.sizeInches) < Number(previousTierSize?.sizeInches)
        );
      });

      return (
        <>
          <QuestionContent>
            <QuestionHeader>
              {isMultiTierSizeQuestion
                ? t(sizeTierIndex === 0 ? "selectBaseLayer" : "selectNextLayer")
                : t(currentQuestion.name)}
            </QuestionHeader>
            <QuestionSubtext>
              {isMultiTierSizeQuestion ? (
                t("pleaseSelectAtLeastTwoLayers")
              ) : t(currentQuestion.subtext) || (
                currentQuestion?.type == "single" ? (
                  t("chooseAny") + " 1 " + t("optionSmallLetter")
                ) : currentQuestion?.type == "multiple" && currentQuestion?.min == 0 && currentQuestion?.max == 0 ? (
                  t("chooseMutiple")
                ) : currentQuestion?.type == "multiple" && currentQuestion?.min == 0 && currentQuestion?.max != 0 ? (
                  t("chooseMaxOption").replace("[VALUE]", currentQuestion?.max)
                ) : currentQuestion?.type == "multiple" && currentQuestion?.min != 0 && currentQuestion?.max == 0 ? (
                  t("chooseMinOption").replace("[VALUE]", currentQuestion?.min)
                ) : currentQuestion?.type == "multiple" && currentQuestion?.min != 0 && currentQuestion?.max != 0 ? (
                  t("cooseMinMAxOption").replace("[MIN]", currentQuestion?.min).replace("[MAX]", currentQuestion?.max)
                ) : null
              )}
            </QuestionSubtext>

            {currentQuestion.type === "quantity" ? (
              <OptionsGrid style={{ gridTemplateColumns: '1fr', maxWidth: '600px' }}>
                {currentQuestion?.options.map((option, index) => {
                  const qty = getQuantity(currentQuestion.id, option.id);
                  return (
                    <QuantityOptionBox
                      key={index}
                      $isSelected={qty > 0}
                    >
                      <OptionImageWrapper>
                        <OptionImage
                          src={getOptionImage(option, currentQuestion.id)}
                          alt={option.name}
                          onError={(e) => {
                            e.target.src = '/images/roundCake.png';
                          }}
                        />
                      </OptionImageWrapper>
                      <QuantityControlArea $isSelected={qty > 0}>
                        <QuantityLeftSide>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <OptionName>{t(option.name)}</OptionName>
                            <OptionPriceTag>
                              <SarIcon src="/images/Groupsar2.svg" alt="SAR" />
                              {option.price}
                            </OptionPriceTag>
                          </div>
                          <QuantityCounter>
                            <CounterButton
                              onClick={() => handleQuantityDecrement(currentQuestion.id, option.id)}
                              disabled={qty === 0}
                            >
                              −
                            </CounterButton>
                            <CounterValue>{qty}</CounterValue>
                            <CounterButton
                              onClick={() => handleQuantityIncrement(currentQuestion.id, option.id)}
                              disabled={qty >= (currentQuestion.maxQuantity ?? 3)}
                            >
                              +
                            </CounterButton>
                          </QuantityCounter>
                          {option?.subtext && (
                            <QuantitySubtext>{t(option.subtext)}</QuantitySubtext>
                          )}
                        </QuantityLeftSide>
                      </QuantityControlArea>
                    </QuantityOptionBox>
                  );
                })}
              </OptionsGrid>
            ) : currentQuestion.type === "message" ? (
              <MessageInputBox $hasMessage={hasMessageCard()}>
                <MessageLabel>
                  {t(currentQuestion?.options?.[0]?.name || "")}
                  <OptionPriceTag>
                    <SarIcon src="/images/Groupsar2.svg" alt="SAR" />
                    {currentQuestion?.options?.[0]?.price || 0}
                  </OptionPriceTag>
                </MessageLabel>
                <MessageTextarea
                  placeholder={t("typeYourCardMessageHere")}
                  value={messageCardText}
                  onChange={(e) => setMessageCardText(e.target.value)}
                />
              </MessageInputBox>
            ) : (
              <OptionsGrid>
                {currentQuestionOptions.map((option, index) => {
                  const isOptionSelected = isMultiTierSizeQuestion
                    ? selectedSizeIds[sizeTierIndex] === option?.id
                    : checkedCheckbox(currentQuestion, option?.id);

                  return (
                    <OptionBox
                      key={index}
                      $isSelected={isOptionSelected}
                      $isDisabled={isMultiTierSizeQuestion && isSizeOptionDisabled(option)}
                      onClick={() =>
                        isMultiTierSizeQuestion
                          ? handleSizeOptionSelect(option?.id)
                          : addCartItemsState(currentQuestion, option?.id)
                      }
                    >
                      <OptionImageWrapper
                        style={
                          currentQuestion.id === 1
                            ? { padding: option.id === 11 ? '48px' : option.id === 12 ? '24px' : '0px' }
                            : {}
                        }
                      >
                        <OptionImage
                          src={getOptionImage(option, currentQuestion.id)}
                          alt={option.name}
                          onError={(e) => {
                            e.target.src = '/images/roundCake.png';
                          }}
                        />
                      </OptionImageWrapper>
                      <OptionInfo $isSelected={isOptionSelected}>
                        <OptionLeftSide>
                          <div style={{ pointerEvents: "none", display: "flex" }}>
                            {(currentQuestion?.type == "single" ||
                              currentQuestion?.id === MULTI_LAYER_QUESTION_ID ||
                              isMultiTierSizeQuestion) ? (
                              <Radio
                                className="w-5 h-5"
                                checked={isOptionSelected}
                                name={
                                  isMultiTierSizeQuestion
                                    ? `custom-cake-size-tier-${sizeTierIndex}`
                                    : `custom-cake-question-${currentQuestion?.id}`
                                }
                                id={`custom-cake-option-${currentQuestion?.id}-${option?.id}`}
                                value={option?.id}
                                readOnly
                              />
                            ) : (
                              <CheckboxCustom
                                className="w-5 h-5"
                                checked={checkedCheckbox(currentQuestion, option?.id)}
                                value={option?.id}
                                inputClassName="w-5 h-5 !p-0"
                                readOnly
                              />
                            )}
                          </div>
                          <OptionTextContent>
                            <OptionName>{t(option.name)}</OptionName>
                            {currentQuestion.id !== 1 && option?.subtext && (
                              <OptionSubtext>{t(option.subtext)}</OptionSubtext>
                            )}
                          </OptionTextContent>
                        </OptionLeftSide>
                        <OptionPriceTag>
                          <SarIcon src="/images/Groupsar2.svg" alt="SAR" />
                          {isMultiTierSizeQuestion && sizeTierIndex > 0
                            ? option.subPrice ?? option.price
                            : option.price}
                        </OptionPriceTag>
                      </OptionInfo>
                    </OptionBox>
                  );
                })}
              </OptionsGrid>
            )}
          </QuestionContent>

          <NavButtonsContainer style={{ flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '20px', width: '100%' }}>
              <NavBtn
                $variant="secondary"
                onClick={handlePreviousStep}
              >
                {`← ${t("back")}`}
              </NavBtn>
              <NavBtn
                onClick={handleNextStep}
                disabled={!isCurrentStepValid()}
              >
                  {activeId === wizardQuestions.length - 1
                  ? `${t("placeOrder")} →`
                  : (wizardQuestions[activeId]?.min === 0 && wizardQuestions[activeId]?.type !== "message")
                    ? isOptionSelectedForStep(activeId)
                      ? ` ${t("nextStep")} →`
                      : ` ${t("skip")} →`
                    : ` ${t("nextStep")} →`
                }
              </NavBtn>
            </div>

            {/* {activeId === STATIC_QUESTIONS.length - 1 && (
              <NavBtn
                $variant="secondary"
                onClick={() => {
                  if (!auth || auth?.id === 0) {
                    navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
                  } else {
                    saveForLater();
                  }
                }}
                style={{ width: '100%' }}
              >
                🔖 {t("saveForLater")}
              </NavBtn>
            )} */}
          </NavButtonsContainer>
        </>
      );
    }

    return null;
  };

  useEffect(() => {
    if (!isStorageHydrated) {
      return;
    }
    saveToLocalStorage();
  }, [isStorageHydrated, selectedData, allSelectedImages, reference, textOnCake, activeId, completedSteps, totalPrice, selectedShape, sizeTierIndex, quantities, messageCardText]);

  useEffect(() => {
    setTotalPrice(getTotalPrice());
  }, [selectedData, quantities, messageCardText, textOnCake]);

  useEffect(() => {
    loadFromLocalStorage();

    const saved = localStorage.getItem("pendingOrderData");
    if (saved) {
      const parsed = JSON.parse(saved);
      const legacyFormData = parsed?.formData;

      if (legacyFormData?.customCakeDetails) {
        const legacyCakeDetails = legacyFormData.customCakeDetails.cake_details || [];
        const looksLikeUiState = legacyCakeDetails.every((group) =>
          wizardQuestions.some((question) => question.id === group?.group_id)
        );

        if (looksLikeUiState) {
          setAllInputData(legacyFormData);
          setTextOnCake(legacyFormData.customCakeDetails.text_on_cake || "");
          setReference(legacyFormData.customCakeDetails.image_link || "");
          setAllSelectedImages(legacyFormData.customCakeDetails.upload_image_links || []);
          setSelectedData(legacyCakeDetails);
          setTotalPrice(Number(legacyFormData.totalPrice || 0));
        }
      }
    }

    setIsStorageHydrated(true);
  }, []);

  useEffect(() => {
    if (!imageForCrop) {
      const imageUploadField = document.getElementById("image-upload-field");
      if (imageUploadField) {
        imageUploadField.value = "";
      }
    }
  }, [imageForCrop]);

  useEffect(() => {
    CUSTOM.trackCustomCake({ cake_type: "Custom" });
    getQuestionList();
  }, []);

  return (
    <>
      <PageWrapper>
        <GlobalStyle />

        <Header>
          <PageTitle>{t("designYourOwnCake")}</PageTitle>
          <PageSubtitle>{t("customCakePageSubtitle")}</PageSubtitle>
        </Header>

        {/* Progress Stepper */}
        <StepperSection>
          <StepperWrapper>
            {stepLabels.map((label, index) => {
              const stepIndex = index - 1;
              const isCompleted = completedSteps.includes(stepIndex);
              const isActive = activeId === stepIndex;

              return (
                <div key={index} style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  {index > 0 && <StepLine $isCompleted={completedSteps.includes(stepIndex - 1)} />}
                  <StepContainer>
                    <StepCircle $isCompleted={isCompleted} $isActive={isActive}>
                      {isCompleted && !isActive ? (
                        <StepIconImg
                          src="/images/checkDone.svg"
                          alt={t("stepCompleted")}
                          $isCompleted={true}
                        />
                      ) : (
                        <StepIconImg
                          src={getStepIcon(index)}
                          alt={label}
                          $isCompleted={isCompleted}
                        />
                      )}
                    </StepCircle>
                    <StepLabel $isCompleted={isCompleted} $isActive={isActive}>
                      {label}
                    </StepLabel>
                  </StepContainer>
                </div>
              );
            })}
          </StepperWrapper>
        </StepperSection>

        {/* Mobile Step Header - Only visible on mobile */}
        <MobileStepHeader>
          <MobileStepInfo>
            <MobileStepIcon
              src={getStepIcon(activeId + 1)}
              alt={getCurrentStepName()}
            />
            <MobileStepText>
              <MobileStepTitle>{getCurrentStepName()}</MobileStepTitle>
              <MobileStepCounter>{getCurrentStepDisplay()}</MobileStepCounter>
            </MobileStepText>
          </MobileStepInfo>
          <ViewDetailsBtn onClick={() => setShowMobilePreview(true)}>
            {t("viewDetails")} {isRTL ? '←' : '→'}
          </ViewDetailsBtn>
        </MobileStepHeader>

        <ContentArea>
          <QuestionPanel>
            {renderCurrentStep()}
          </QuestionPanel>

          {/* Desktop Live Preview - Hidden on mobile */}
          <PreviewPanel>
            {renderPreviewContent()}
          </PreviewPanel>
        </ContentArea>

        {/* Mobile Preview Modal */}
        <MobilePreviewModal
          $isOpen={showMobilePreview}
          onClick={() => setShowMobilePreview(false)}
        >
          <MobilePreviewContent onClick={(e) => e.stopPropagation()}>
            <MobilePreviewHeader>
              <MobilePreviewTitle>{t("orderDetails")}</MobilePreviewTitle>
              <CloseModalBtn onClick={() => setShowMobilePreview(false)}>
                ×
              </CloseModalBtn>
            </MobilePreviewHeader>
            <MobilePreviewBody>
              {renderPreviewContent()}
            </MobilePreviewBody>
          </MobilePreviewContent>
        </MobilePreviewModal>

        {imageForCrop && (
          <ImageCropper
            image={imageForCrop}
            allSelectedImages={allSelectedImages}
            setAllSelectedImages={setAllSelectedImages}
            setImageForCrop={setImageForCrop}
          />
        )}
      </PageWrapper>
      <Footer />
    </>
  );
};

export default DesignMyOwnCakeQuestionOnePage;